import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FiBook, FiList, FiVideo, FiCheckCircle, FiLogOut, FiFileText, FiHome, FiEye, FiEyeOff, FiEdit3, FiTrendingUp, FiMic, FiSquare, FiPlay, FiTrash2, FiUpload, FiCheck, FiClipboard, FiCircle, FiAlertCircle, FiInfo, FiClock, FiMenu, FiX, FiPause, FiRefreshCw } from 'react-icons/fi';
import { studentAPI, materialsAPI, API_ORIGIN } from '../../services/api';
import StudentTestsBlocksPage from './StudentTestsBlocksPage';
import StudentHomework from './StudentHomework';
import StudentMaterials from './StudentMaterials';
import ttsService from '../../utils/textToSpeech';
import { parseSentencesFromFullText, expandSentenceRows } from '../../utils/lessonTextSentences';
import { getStudentLessonPath } from '../../utils/lessonUrls';
import './StudentDashboard.css';

/** Макс. длительность записи произношения по предложению, секунды (автоостановка). Для 10 секунд: 10 */
const SPEECH_RECORDING_MAX_SECONDS = 10 * 60;

const StudentDashboard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = window.location;
    const [unreadHomeworkCount, setUnreadHomeworkCount] = React.useState(0);
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

    // Загружаем количество непрочитанных ДЗ
    React.useEffect(() => {
        loadUnreadHomeworkCount();
    }, []);

    // Закрываем мобильное меню при смене маршрута
    React.useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    const loadUnreadHomeworkCount = async () => {
        try {
            const response = await studentAPI.getUnreadHomeworkCount();
            setUnreadHomeworkCount(response.data.count || 0);
        } catch (error) {
            console.error('Ошибка загрузки количества ДЗ:', error);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const menuItems = [
        { path: '/student', icon: <FiHome />, label: 'Главная', exact: true },
        { path: '/student/lessons', icon: <FiBook />, label: 'Уроки' },
        { path: '/student/homework', icon: <FiFileText />, label: 'Дом. задания', badge: unreadHomeworkCount },
        { path: '/student/materials', icon: <FiFileText />, label: 'Материалы' },
        { path: '/student/tests', icon: <FiClipboard />, label: 'Тестирование' },
        { path: '/student/progress', icon: <FiTrendingUp />, label: 'Прогресс' },
        { path: '/student/formulas', icon: <FiCheckCircle />, label: 'Речевые формулы' },
    ];

    const isActive = (path) => {
        if (path === '/student') {
            return location.pathname === '/student' || location.pathname === '/student/';
        }
        return location.pathname.startsWith(path);
    };

    return (
        <div className="student-dashboard">
            {/* Оверлей для мобильного меню */}
            {mobileMenuOpen && (
                <div 
                    className="mobile-overlay active" 
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            {/* Горизонтальное верхнее меню */}
            <nav className="student-top-nav">
                <button 
                    className="mobile-menu-button" 
                    onClick={toggleMobileMenu}
                    aria-label="Меню"
                >
                    {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
                </button>

                <div className="top-nav-brand">
                    <FiBook size={24} />
                    Speaking Drive
                </div>

                <div className={`top-nav-links ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}>
                    {menuItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`top-nav-link ${isActive(item.path) ? 'active' : ''}`}
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            {item.icon}
                            <span>{item.label}</span>
                            {item.badge && item.badge > 0 && (
                                <span className="badge-count">{item.badge}</span>
                            )}
                        </Link>
                    ))}
                </div>

                <div className="top-nav-user">
                    <span className="user-name">{user?.username}</span>
                    <button onClick={handleLogout} className="btn-logout-top">
                        <FiLogOut />
                        <span className="btn-logout-text">Выйти</span>
                    </button>
                </div>
            </nav>

            {/* Основной контент с роутами */}
            <div className="student-main-wrapper">
                <Routes>
                    <Route path="/" element={<StudentHome />} />
                    <Route path="/lessons" element={<StudentLessons />} />
                    <Route path="/lessons/:lessonId" element={<StudentLessonView />} />
                    <Route path="/homework" element={<StudentHomework />} />
                    <Route path="/materials" element={<StudentMaterials />} />
                    <Route path="/materials/lesson/:materialId" element={<StudentMaterialLessonView />} />
                    <Route path="/tests" element={<StudentTestsBlocksPage />} />
                    <Route path="/progress" element={<StudentProgress />} />
                    <Route path="/formulas" element={<StudentFormulas />} />
                </Routes>
            </div>
        </div>
    );
};

const StudentHome = () => {
    return (
        <main className="student-content">
            <div className="container">
                <div className="page-header">
                    <h1>Добро пожаловать!</h1>
                    <p>Выберите раздел для начала обучения</p>
                </div>

                <div className="dashboard-cards">
                    <Link to="/student/lessons" className="dashboard-card">
                        <FiBook size={40} color="#4a90e2" />
                        <h3>Уроки</h3>
                        <p>Изучайте материалы по уровням</p>
                    </Link>

                    <Link to="/student/homework" className="dashboard-card">
                        <FiFileText size={40} color="#f5222d" />
                        <h3>Домашние задания</h3>
                        <p>Выполняйте задания от учителя</p>
                    </Link>

                    <Link to="/student/materials" className="dashboard-card">
                        <FiFileText size={40} color="#13c2c2" />
                        <h3>Материалы</h3>
                        <p>Дополнительные материалы для изучения</p>
                    </Link>

                    <Link to="/student/tests" className="dashboard-card">
                        <FiClipboard size={40} color="#fa8c16" />
                        <h3>Тестирование</h3>
                        <p>Проверьте свои знания</p>
                    </Link>

                    <Link to="/student/progress" className="dashboard-card">
                        <FiTrendingUp size={40} color="#7b68ee" />
                        <h3>Прогресс</h3>
                        <p>Отслеживайте свои достижения</p>
                    </Link>

                    <Link to="/student/formulas" className="dashboard-card">
                        <FiCheckCircle size={40} color="#52c41a" />
                        <h3>Речевые формулы</h3>
                        <p>Ваш прогресс по формулам</p>
                    </Link>
                </div>
            </div>
        </main>
    );
};

const StudentLessons = () => {
    const navigate = useNavigate();
    const [areas, setAreas] = React.useState([]);
    const [lessons, setLessons] = React.useState([]);
    const [selectedCategory, setSelectedCategory] = React.useState('');
    const [selectedArea, setSelectedArea] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [playbackSpeed, setPlaybackSpeed] = React.useState(1);
    const [expandedLessons, setExpandedLessons] = React.useState({});
    const [lessonBlocks, setLessonBlocks] = React.useState({});
    const [sidebarExpandedLessons, setSidebarExpandedLessons] = React.useState({});
    const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

    React.useEffect(() => {
        if (selectedCategory) {
            loadAreas();
        }
    }, [selectedCategory]);

    React.useEffect(() => {
        if (selectedArea) {
            loadLessons();
        }
    }, [selectedArea, selectedCategory]);

    const loadAreas = async () => {
        try {
            const response = await studentAPI.getAreas({ category: selectedCategory });
            setAreas(response.data.areas);
            setLoading(false);
        } catch (error) {
            console.error('Ошибка загрузки областей:', error);
            setLoading(false);
        }
    };

    const loadLessons = async () => {
        try {
            const levelsRes = await studentAPI.getLevels(selectedArea);
            const levels = levelsRes.data.levels || [];
            const lessonsPromises = levels.map(level =>
                studentAPI.getLessons(level.id).then(res => res.data.lessons || [])
            );
            const allLessonsArrays = await Promise.all(lessonsPromises);
            const allLessons = allLessonsArrays.flat().filter(l => l.category === selectedCategory);
            setLessons(allLessons);
        } catch (error) {
            console.error('Ошибка загрузки уроков:', error);
        }
    };

    const toggleLessonExpand = async (lessonId) => {
        const isExpanded = expandedLessons[lessonId];
        setExpandedLessons(prev => ({ ...prev, [lessonId]: !isExpanded }));
        
        // Загружаем блоки, если еще не загружены
        if (!isExpanded && !lessonBlocks[lessonId]) {
            try {
                const response = await studentAPI.getLesson(lessonId);
                setLessonBlocks(prev => ({ ...prev, [lessonId]: response.data.blocks || [] }));
            } catch (error) {
                console.error('Ошибка загрузки блоков урока:', error);
            }
        }
    };

    const toggleSidebarLessonExpand = async (lessonId) => {
        const isExpanded = sidebarExpandedLessons[lessonId];
        setSidebarExpandedLessons(prev => ({ ...prev, [lessonId]: !isExpanded }));
        
        // Загружаем блоки, если еще не загружены
        if (!isExpanded && !lessonBlocks[lessonId]) {
            try {
                const response = await studentAPI.getLesson(lessonId);
                setLessonBlocks(prev => ({ ...prev, [lessonId]: response.data.blocks || [] }));
            } catch (error) {
                console.error('Ошибка загрузки блоков урока:', error);
            }
        }
    };

    const handleBlockClick = (lessonItem, blockIndex) => {
        navigate(getStudentLessonPath(lessonItem, blockIndex));
    };

    if (loading) {
        return (
            <main className="student-content">
                <div className="loading">Загрузка...</div>
            </main>
        );
    }

    return (
        <>
            {/* Оверлей для мобильного сайдбара */}
            {mobileSidebarOpen && (
                <div 
                    className="mobile-overlay active" 
                    onClick={() => setMobileSidebarOpen(false)}
                />
            )}

            {/* Боковое меню */}
            <aside className={`lesson-sidebar ${mobileSidebarOpen ? 'mobile-sidebar-open' : ''}`}>
                <div className="lesson-sidebar-header">
                    <div className="lesson-sidebar-tabs">
                        <button 
                            className={`lesson-sidebar-tab ${selectedCategory === 'grammar' ? 'active' : ''}`}
                            onClick={async () => {
                                setSelectedCategory('grammar');
                                const areasRes = await studentAPI.getAreas({ category: 'grammar' });
                                setAreas(areasRes.data.areas || []);
                                setSelectedArea('');
                                setLessons([]);
                            }}
                        >
                            <FiBook size={14} />
                            Грамматика
                        </button>
                        <button 
                            className={`lesson-sidebar-tab ${selectedCategory === 'vocabulary' ? 'active' : ''}`}
                            onClick={async () => {
                                setSelectedCategory('vocabulary');
                                const areasRes = await studentAPI.getAreas({ category: 'vocabulary' });
                                setAreas(areasRes.data.areas || []);
                                setSelectedArea('');
                                setLessons([]);
                            }}
                        >
                            <FiFileText size={14} />
                            Лексика
                        </button>
                    </div>
                </div>

                <div className="lesson-sidebar-areas">
                    {selectedCategory && (
                        <>
                            <div style={{ padding: '8px 0 5px', fontSize: '9px', fontWeight: '600', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Области обучения
                            </div>
                            {areas.map(area => (
                                <div
                                    key={area.id}
                                    className={`lesson-sidebar-area ${selectedArea === area.id ? 'active' : ''}`}
                                    onClick={() => {
                                        setSelectedArea(area.id);
                                    }}
                                >
                                    {area.name}
                                </div>
                            ))}
                        </>
                    )}
                    {!selectedCategory && (
                        <div style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#999' }}>
                            Выберите категорию выше
                        </div>
                    )}
                    
                    {/* Контролы скорости */}
                    <div style={{ 
                        marginTop: '10px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        borderTop: '1px solid #e8e8e8'
                    }}>
                        <label style={{ fontSize: '9px', color: '#999', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>
                            Скорость воспроизведения
                        </label>
                        <select 
                            value={playbackSpeed} 
                            onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
                            style={{ 
                                width: '100%',
                                padding: '5px',
                                borderRadius: '4px',
                                border: '1px solid #e0e0e0',
                                fontSize: '11px',
                                background: 'white',
                                cursor: 'pointer'
                            }}
                        >
                            <option value="0.5">0.5x (медленно)</option>
                            <option value="0.75">0.75x</option>
                            <option value="1">1x (нормально)</option>
                            <option value="1.25">1.25x</option>
                            <option value="1.5">1.5x (быстро)</option>
                        </select>
                    </div>
                </div>

                <div className="lesson-sidebar-lessons">
                    {selectedArea && (
                        <>
                            <div style={{ padding: '0 0 10px', fontSize: '11px', fontWeight: '600', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Уроки
                            </div>
                            {lessons.length > 0 ? (
                                lessons.map((lessonItem, index) => {
                                    const levelLabel = lessonItem.level_name || `Уровень ${lessonItem.level_id || ''}`.trim();
                                    const previousLesson = lessons[index - 1];
                                    const previousLevelLabel = previousLesson
                                        ? previousLesson.level_name || `Уровень ${previousLesson.level_id || ''}`.trim()
                                        : null;
                                    const showLevelDivider = levelLabel && levelLabel !== previousLevelLabel;

                                    return (
                                    <React.Fragment key={lessonItem.id}>
                                        {showLevelDivider && (
                                            <div className="lesson-sidebar-level-divider">
                                                {levelLabel}
                                            </div>
                                        )}
                                        <div style={{ marginBottom: '4px' }}>
                                        <div
                                            className="lesson-sidebar-lesson"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <div 
                                                onClick={() => navigate(getStudentLessonPath(lessonItem))}
                                                style={{ flex: 1 }}
                                            >
                                                <div className="lesson-sidebar-lesson-title">{lessonItem.title}</div>
                                                <div className="lesson-sidebar-lesson-level">{lessonItem.level_name}</div>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleSidebarLessonExpand(lessonItem.id);
                                                }}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    color: '#666',
                                                    transform: sidebarExpandedLessons[lessonItem.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.2s'
                                                }}
                                            >
                                                ▼
                                            </button>
                                        </div>
                                        
                                        {/* Блоки урока в сайдбаре */}
                                        {sidebarExpandedLessons[lessonItem.id] && lessonBlocks[lessonItem.id] && (
                                            <div style={{ paddingLeft: '15px', marginTop: '5px' }}>
                                                {lessonBlocks[lessonItem.id].map((block, blockIdx) => (
                                                    <div
                                                        key={blockIdx}
                                                        onClick={() => handleBlockClick(lessonItem, blockIdx)}
                                                        style={{
                                                            padding: '8px 10px',
                                                            marginBottom: '4px',
                                                            cursor: 'pointer',
                                                            borderRadius: '4px',
                                                            background: '#f8f9fa',
                                                            fontSize: '12px',
                                                            color: '#666',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.background = '#e9ecef';
                                                            e.currentTarget.style.color = '#1976d2';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.background = '#f8f9fa';
                                                            e.currentTarget.style.color = '#666';
                                                        }}
                                                    >
                                                        {block.title}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        </div>
                                    </React.Fragment>
                                    );
                                })
                            ) : (
                                <div style={{ padding: '20px', textAlign: 'center', fontSize: '13px', color: '#999' }}>
                                    Нет уроков
                                </div>
                            )}
                        </>
                    )}
                    {!selectedArea && (
                        <div style={{ padding: '20px', textAlign: 'center', fontSize: '13px', color: '#999' }}>
                            Выберите область обучения
                        </div>
                    )}
                </div>
            </aside>

            {/* Основной контент */}
            <main className="student-content" style={{ padding: '30px' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    {!selectedCategory && (
                        <>
                            <div className="page-header">
                                <h1>Уроки</h1>
                                <p>Выберите категорию для начала</p>
                            </div>

                            {/* Выбор категории */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginTop: '30px' }}>
                                <div 
                                    className="card" 
                                    style={{ 
                                        cursor: 'pointer', 
                                        transition: 'all 0.3s',
                                        border: '2px solid #e3f2fd',
                                        background: 'linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)'
                                    }}
                                    onClick={() => setSelectedCategory('grammar')}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.borderColor = '#1976d2';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.borderColor = '#e3f2fd';
                                    }}
                                >
                                    <div style={{ textAlign: 'center' }}>
                                        <FiBook size={48} color="#1976d2" />
                                        <h2 style={{ marginTop: '15px', color: '#1976d2' }}>Грамматика</h2>
                                        <p style={{ color: '#666', marginTop: '10px' }}>Изучайте грамматические правила и структуры</p>
                                    </div>
                                </div>

                                <div 
                                    className="card" 
                                    style={{ 
                                        cursor: 'pointer', 
                                        transition: 'all 0.3s',
                                        border: '2px solid #fff3e0',
                                        background: 'linear-gradient(135deg, #fff3e0 0%, #f8f9fa 100%)'
                                    }}
                                    onClick={() => setSelectedCategory('vocabulary')}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.borderColor = '#f57c00';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.borderColor = '#fff3e0';
                                    }}
                                >
                                    <div style={{ textAlign: 'center' }}>
                                        <FiFileText size={48} color="#f57c00" />
                                        <h2 style={{ marginTop: '15px', color: '#f57c00' }}>Лексика</h2>
                                        <p style={{ color: '#666', marginTop: '10px' }}>Расширяйте словарный запас</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {selectedCategory && (
                        <>
                            <div className="page-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <button
                                        onClick={() => {
                                            setSelectedCategory('');
                                            setSelectedArea('');
                                            setLessons([]);
                                        }}
                                        style={{
                                            background: 'transparent',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '8px',
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            fontSize: '14px',
                                            color: '#666'
                                        }}
                                    >
                                        ← Назад
                                    </button>
                                    <div>
                                        <h1>{selectedCategory === 'grammar' ? 'Грамматика' : 'Лексика'}</h1>
                                        <p>
                                            {selectedArea 
                                                ? `Уроки: ${areas.find(a => a.id === selectedArea)?.name || ''}` 
                                                : 'Выберите область обучения'
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Отображаем области обучения, если не выбрана конкретная область */}
                            {!selectedArea && areas.length > 0 && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px', marginTop: '20px' }}>
                                    {areas.map(area => (
                                        <div 
                                            key={area.id}
                                            className="card" 
                                            style={{ 
                                                cursor: 'pointer', 
                                                transition: 'all 0.3s',
                                                border: '2px solid #e8e8e8',
                                                padding: '30px'
                                            }}
                                            onClick={() => setSelectedArea(area.id)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-4px)';
                                                e.currentTarget.style.borderColor = selectedCategory === 'grammar' ? '#1976d2' : '#f57c00';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.borderColor = '#e8e8e8';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                        >
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{
                                                    width: '60px',
                                                    height: '60px',
                                                    borderRadius: '50%',
                                                    background: selectedCategory === 'grammar' ? '#e3f2fd' : '#fff3e0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    margin: '0 auto 15px'
                                                }}>
                                                    <FiBook 
                                                        size={28} 
                                                        color={selectedCategory === 'grammar' ? '#1976d2' : '#f57c00'} 
                                                    />
                                                </div>
                                                <h3 style={{ 
                                                    fontSize: '18px', 
                                                    fontWeight: '600',
                                                    color: '#2c3e50',
                                                    marginBottom: '10px'
                                                }}>
                                                    {area.name}
                                                </h3>
                                                {area.description && (
                                                    <p style={{ 
                                                        fontSize: '14px', 
                                                        color: '#666',
                                                        lineHeight: '1.6'
                                                    }}>
                                                        {area.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {selectedArea && lessons.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    {lessons.map(lesson => (
                                        <div 
                                            key={lesson.id} 
                                            className="card" 
                                            style={{ 
                                                transition: 'all 0.3s',
                                                border: expandedLessons[lesson.id] ? '2px solid #1976d2' : '2px solid #e8e8e8'
                                            }}
                                        >
                                            {/* Заголовок урока */}
                                            <div 
                                                style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'space-between',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ 
                                                        display: 'inline-block',
                                                        padding: '4px 12px',
                                                        background: selectedCategory === 'grammar' ? '#e3f2fd' : '#fff3e0',
                                                        color: selectedCategory === 'grammar' ? '#1976d2' : '#f57c00',
                                                        borderRadius: '12px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        marginBottom: '12px'
                                                    }}>
                                                        {selectedCategory === 'grammar' ? 'ГРАММАТИКА' : 'ЛЕКСИКА'}
                                                    </div>
                                                    <h3 style={{ marginBottom: '10px', fontSize: '20px' }}>{lesson.title}</h3>
                                                    {lesson.description && (
                                                        <p style={{ color: '#7f8c8d', fontSize: '14px', lineHeight: '1.6' }}>
                                                            {lesson.description}
                                                        </p>
                                                    )}
                                                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e8e8e8' }}>
                                                        <small style={{ color: '#999' }}>Уровень: {lesson.level_name}</small>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => toggleLessonExpand(lesson.id)}
                                                    style={{
                                                        background: expandedLessons[lesson.id] ? '#1976d2' : '#f0f7ff',
                                                        color: expandedLessons[lesson.id] ? 'white' : '#1976d2',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        padding: '12px 20px',
                                                        borderRadius: '8px',
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        transition: 'all 0.2s',
                                                        marginLeft: '20px'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!expandedLessons[lesson.id]) {
                                                            e.currentTarget.style.background = '#e3f2fd';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!expandedLessons[lesson.id]) {
                                                            e.currentTarget.style.background = '#f0f7ff';
                                                        }
                                                    }}
                                                >
                                                    {expandedLessons[lesson.id] ? 'Скрыть блоки' : 'Показать блоки'}
                                                    <span style={{
                                                        transform: expandedLessons[lesson.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.2s',
                                                        display: 'inline-block'
                                                    }}>
                                                        ▼
                                                    </span>
                                                </button>
                                            </div>

                                            {/* Блоки урока */}
                                            {expandedLessons[lesson.id] && lessonBlocks[lesson.id] && (
                                                <div style={{ 
                                                    marginTop: '20px', 
                                                    paddingTop: '20px', 
                                                    borderTop: '2px solid #e8e8e8',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '8px'
                                                }}>
                                                    {lessonBlocks[lesson.id].length > 0 ? (
                                                        lessonBlocks[lesson.id].map((block, blockIdx) => (
                                                            <div
                                                                key={blockIdx}
                                                                onClick={() => handleBlockClick(lesson, blockIdx)}
                                                                style={{
                                                                    padding: '12px 16px',
                                                                    background: '#f8f9fa',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s',
                                                                    border: '1px solid #e8e8e8'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.background = '#e9ecef';
                                                                    e.currentTarget.style.borderColor = '#1976d2';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.background = '#f8f9fa';
                                                                    e.currentTarget.style.borderColor = '#e8e8e8';
                                                                }}
                                                            >
                                                                <div style={{ 
                                                                    fontSize: '15px', 
                                                                    fontWeight: '500',
                                                                    color: '#2c3e50'
                                                                }}>
                                                                    {block.title}
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div style={{ 
                                                            padding: '20px', 
                                                            textAlign: 'center', 
                                                            color: '#999',
                                                            fontStyle: 'italic'
                                                        }}>
                                                            Блоки для этого урока еще не добавлены
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : selectedArea ? (
                                <div className="card">
                                    <p style={{ textAlign: 'center', color: '#999' }}>
                                        Уроки категории "{selectedCategory === 'grammar' ? 'Грамматика' : 'Лексика'}" для этой области еще не созданы
                                    </p>
                                </div>
                            ) : null}
                        </>
                    )}
                </div>

                {/* Кнопка для открытия мобильного сайдбара */}
                {selectedCategory && (
                    <button 
                        className="mobile-sidebar-toggle" 
                        onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
                        aria-label="Меню уроков"
                    >
                        <FiList size={24} />
                    </button>
                )}
            </main>
        </>
    );
};

const StudentProgress = () => {
    const [loading, setLoading] = React.useState(true);
    const [stats, setStats] = React.useState({
        completedLessons: 0,
        totalLessons: 0,
        testResults: [],
        audioRecordings: 0,
        formulasStats: { total: 0, mastered: 0, partial: 0 },
        recentActivity: []
    });

    React.useEffect(() => {
        loadProgressData();
    }, []);

    const loadProgressData = async () => {
        try {
            setLoading(true);
            
            // Загружаем все данные параллельно
            const [formulasRes, testResultsRes, audioRes] = await Promise.all([
                studentAPI.getFormulas(),
                studentAPI.getMyTestResults(),
                studentAPI.getAudioRecordings()
            ]);
            
            const formulas = formulasRes.data.formulas || [];
            const testResults = (testResultsRes.data.results || []).map(r => ({
                ...r,
                percentage: Number(r.percentage)
            }));
            const audioRecordings = audioRes.data.recordings || [];
            
            setStats({
                completedLessons: 0, // Можно добавить отслеживание
                totalLessons: 0,
                testResults: testResults.slice(0, 5), // Последние 5 результатов
                audioRecordings: audioRecordings.length,
                formulasStats: {
                    total: formulas.length,
                    mastered: formulas.filter(f => f.status === 2).length,
                    partial: formulas.filter(f => f.status === 1).length
                },
                recentActivity: [
                    ...testResults.slice(0, 3).map(t => ({
                        type: 'test',
                        title: t.test_title || 'Тест',
                        date: t.completed_at,
                        score: t.writing_checked && t.final_percentage !== null ? Number(t.final_percentage) : Number(t.percentage),
                        passed: t.writing_checked && t.final_percentage !== null ? (t.final_percentage >= t.passing_score) : t.passed,
                        isChecking: t.has_writing && !t.writing_checked
                    })),
                    ...audioRecordings.slice(0, 2).map(a => ({
                        type: 'audio',
                        title: a.sentence_en || a.sentence_ru || 'Аудиозапись',
                        date: a.created_at,
                        hasFeeback: a.feedback_id ? true : false
                    }))
                ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5)
            });
            
        } catch (error) {
            console.error('Ошибка загрузки прогресса:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <main className="student-content">
                <div className="loading">Загрузка...</div>
            </main>
        );
    }

    const passedTests = stats.testResults.filter(t => {
        if (t.has_writing && !t.writing_checked) return false;
        const displayPercentage = t.writing_checked && t.final_percentage !== null 
            ? Number(t.final_percentage) 
            : Number(t.percentage);
        return displayPercentage >= t.passing_score;
    }).length;
    
    const avgScore = stats.testResults.length > 0 
        ? (stats.testResults.reduce((sum, t) => {
            const displayPercentage = t.writing_checked && t.final_percentage !== null 
                ? Number(t.final_percentage) 
                : Number(t.percentage);
            return sum + displayPercentage;
        }, 0) / stats.testResults.length).toFixed(1)
        : 0;

    return (
        <main className="student-content">
            <div className="container">
            <div className="page-header">
                <h1>Мой прогресс</h1>
                <p>Отслеживайте свои достижения в обучении</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                <div className="card" style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '25px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <FiClipboard size={20} />
                        <div style={{ fontSize: '14px', opacity: 0.9 }}>Тестирование</div>
                    </div>
                    <div style={{ fontSize: '36px', fontWeight: '700', marginBottom: '5px' }}>
                        {passedTests}/{stats.testResults.length}
                    </div>
                    <div style={{ fontSize: '13px', opacity: 0.8 }}>
                        {stats.testResults.length > 0 ? `Средний балл: ${avgScore}%` : 'Пройдите первый тест'}
                    </div>
                </div>

                <div className="card" style={{ 
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    padding: '25px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <FiMic size={20} />
                        <div style={{ fontSize: '14px', opacity: 0.9 }}>Аудиозаписи</div>
                    </div>
                    <div style={{ fontSize: '36px', fontWeight: '700', marginBottom: '5px' }}>
                        {stats.audioRecordings}
                    </div>
                    <div style={{ fontSize: '13px', opacity: 0.8 }}>
                        {stats.audioRecordings > 0 ? 'отправлено преподавателю' : 'Начните практиковать'}
                    </div>
                </div>

                <div className="card" style={{ 
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    color: 'white',
                    padding: '25px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <FiCheckCircle size={20} />
                        <div style={{ fontSize: '14px', opacity: 0.9 }}>Речевые формулы</div>
                    </div>
                    <div style={{ fontSize: '36px', fontWeight: '700', marginBottom: '5px' }}>
                        {stats.formulasStats.mastered}/{stats.formulasStats.total}
                    </div>
                    <div style={{ fontSize: '13px', opacity: 0.8 }}>
                        {stats.formulasStats.partial > 0 && `${stats.formulasStats.partial} требуют повторения`}
                        {stats.formulasStats.partial === 0 && stats.formulasStats.mastered > 0 && 'Отлично!'}
                        {stats.formulasStats.total === 0 && 'Скоро появятся'}
                    </div>
                </div>
            </div>

            {/* Последние результаты тестов */}
            {stats.testResults.length > 0 && (
                <div className="card" style={{ marginBottom: '20px' }}>
                    <h2 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FiClipboard /> Последние тесты
                    </h2>
                    <div style={{ display: 'grid', gap: '12px' }}>
                        {stats.testResults.map((result, idx) => {
                            const displayPercentage = result.writing_checked && result.final_percentage !== null 
                                ? Number(result.final_percentage) 
                                : Number(result.percentage);
                            const isPassed = result.writing_checked && result.final_percentage !== null
                                ? displayPercentage >= result.passing_score
                                : result.passed;
                            
                            return (
                                <div key={idx} style={{
                                    padding: '15px',
                                    border: '1px solid #e8e8e8',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    background: result.has_writing && !result.writing_checked ? '#fff7e6' : (
                                        isPassed ? '#f6ffed' : '#fff2f0'
                                    )
                                }}>
                                    <div>
                                        <div style={{ fontWeight: '500', marginBottom: '5px' }}>{result.test_title}</div>
                                        <div style={{ fontSize: '13px', color: '#666' }}>
                                            {new Date(result.completed_at).toLocaleDateString('ru-RU')}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        {result.has_writing && !result.writing_checked ? (
                                            <div style={{
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                color: '#faad14',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                <FiClock size={16} /> На проверке
                                            </div>
                                        ) : (
                                            <>
                                                <div style={{ 
                                                    fontSize: '24px', 
                                                    fontWeight: 'bold',
                                                    color: isPassed ? '#52c41a' : '#f5222d'
                                                }}>
                                                    {displayPercentage.toFixed(0)}%
                                                </div>
                                                <div style={{ 
                                                    fontSize: '12px',
                                                    color: isPassed ? '#52c41a' : '#f5222d',
                                                    fontWeight: '500'
                                                }}>
                                                    {isPassed ? 'Пройден' : 'Не пройден'}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Последняя активность */}
            {stats.recentActivity.length > 0 && (
                <div className="card">
                    <h2 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FiTrendingUp /> Последняя активность
                    </h2>
                    <div style={{ display: 'grid', gap: '10px' }}>
                        {stats.recentActivity.map((activity, idx) => (
                            <div key={idx} style={{
                                padding: '12px',
                                border: '1px solid #e8e8e8',
                                borderRadius: '6px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {activity.type === 'test' ? (
                                        <FiClipboard size={18} color="#4a90e2" />
                                    ) : (
                                        <FiMic size={18} color="#f5576c" />
                                    )}
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: '500' }}>{activity.title}</div>
                                        <div style={{ fontSize: '12px', color: '#999' }}>
                                            {new Date(activity.date).toLocaleString('ru-RU')}
                                        </div>
                                    </div>
                                </div>
                                {activity.type === 'test' && (
                                    <>
                                        {activity.isChecking ? (
                                            <div style={{ 
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: '500',
                                                background: '#fff7e6',
                                                color: '#faad14',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}>
                                                <FiClock size={12} /> На проверке
                                            </div>
                                        ) : (
                                            <div style={{ 
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: '500',
                                                background: activity.passed ? '#f6ffed' : '#fff2f0',
                                                color: activity.passed ? '#52c41a' : '#f5222d'
                                            }}>
                                                {activity.score.toFixed(0)}%
                                            </div>
                                        )}
                                    </>
                                )}
                                {activity.type === 'audio' && activity.hasFeeback && (
                                    <div style={{ 
                                        padding: '4px 12px',
                                        borderRadius: '12px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        background: '#f6ffed',
                                        color: '#52c41a',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        <FiCheck size={12} /> Проверено
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {stats.recentActivity.length === 0 && stats.testResults.length === 0 && (
                <div className="card">
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
                        <FiTrendingUp size={48} style={{ marginBottom: '15px', opacity: 0.5 }} />
                        <p>Начните свое обучение!</p>
                        <p style={{ fontSize: '14px', marginTop: '10px' }}>
                            Проходите уроки, тесты и упражнения, чтобы отслеживать свой прогресс
                        </p>
                    </div>
                </div>
            )}
        </div>
    </main>
    );
};

const StudentTexts = () => {
    const [lessons, setLessons] = React.useState([]);
    const [selectedLesson, setSelectedLesson] = React.useState(null);
    const [text, setText] = React.useState(null);
    const [sentences, setSentences] = React.useState([]);
    const [showTranslations, setShowTranslations] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const loadLessonTexts = async (lessonId) => {
        try {
            setLoading(true);
            const response = await studentAPI.getLesson(lessonId);
            setSelectedLesson(response.data.lesson);
            if (response.data.texts && response.data.texts.length > 0) {
                loadText(response.data.texts[0].id);
            }
            setLoading(false);
        } catch (error) {
            console.error('Ошибка загрузки урока:', error);
            setLoading(false);
        }
    };

    const loadText = async (textId) => {
        try {
            const response = await studentAPI.getText(textId);
            setText(response.data.text);
            setSentences(response.data.sentences);
        } catch (error) {
            console.error('Ошибка загрузки текста:', error);
        }
    };

    if (loading) {
        return <div className="loading">Загрузка...</div>;
    }

    return (
        <div className="container">
            <div className="page-header">
                <h1>Тексты</h1>
                <p>Читайте и слушайте тексты с переводом</p>
            </div>

            {!text ? (
                <div className="card">
                    <p style={{ textAlign: 'center', color: '#999' }}>
                        Выберите урок из раздела "Уроки" для просмотра текстов
                    </p>
                </div>
            ) : (
                <>
                    <div className="card" style={{ marginBottom: '20px' }}>
                        <h2 style={{ marginBottom: '10px' }}>{text.title}</h2>
                        <p style={{ color: '#7f8c8d', marginBottom: '15px' }}>
                            {text.lesson_title} | {text.level_name}
                        </p>
                        {text.description && (
                            <p style={{ color: '#555', marginBottom: '15px' }}>{text.description}</p>
                        )}
                        <button 
                            className="btn btn-secondary"
                            onClick={() => setShowTranslations(!showTranslations)}
                            style={{ marginTop: '10px' }}
                        >
                            {showTranslations ? 'Скрыть переводы' : 'Показать переводы'}
                        </button>
                    </div>

                    <div className="card">
                        {sentences.map((sentence, index) => (
                            <div key={sentence.id} style={{
                                padding: '15px',
                                marginBottom: '12px',
                                borderLeft: '3px solid #4a90e2',
                                background: '#f9f9f9',
                                borderRadius: '4px'
                            }}>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                    <div style={{
                                        minWidth: '28px',
                                        height: '28px',
                                        background: '#4a90e2',
                                        color: 'white',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '13px',
                                        fontWeight: 'bold'
                                    }}>
                                        {index + 1}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '8px' }}>
                                            {sentence.sentence_en}
                                        </div>
                                        {showTranslations && sentence.sentence_ru && (
                                            <div style={{ 
                                                fontSize: '14px', 
                                                color: '#52c41a',
                                                paddingTop: '8px',
                                                borderTop: '1px solid #e8e8e8'
                                            }}>
                                                {sentence.sentence_ru}
                                            </div>
                                        )}
                                        {sentence.audio_url && (
                                            <div style={{ marginTop: '8px' }}>
                                                <button 
                                                    style={{
                                                        padding: '4px 12px',
                                                        background: '#52c41a',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    🔊 Прослушать
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const StudentFormulas = () => {
    const [formulas, setFormulas] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [filter, setFilter] = React.useState('all');

    React.useEffect(() => {
        loadFormulas();
    }, []);

    const loadFormulas = async () => {
        try {
            const response = await studentAPI.getFormulas();
            setFormulas(response.data.formulas);
            setLoading(false);
        } catch (error) {
            console.error('Ошибка загрузки формул:', error);
            setLoading(false);
        }
    };

    const getStatusInfo = (status) => {
        switch (status) {
            case 2:
                return { text: 'Усвоено', color: '#52c41a', icon: <FiCheckCircle /> };
            case 1:
                return { text: 'Есть проблема', color: '#faad14', icon: <FiAlertCircle /> };
            default:
                return { text: 'Не начато', color: '#d9d9d9', icon: <FiCircle /> };
        }
    };

    const filteredFormulas = formulas.filter(f => {
        if (filter === 'all') return true;
        if (filter === 'mastered') return f.status === 2;
        if (filter === 'partial') return f.status === 1;
        if (filter === 'not-started') return !f.status || f.status === 0;
        return true;
    });

    const stats = {
        total: formulas.length,
        mastered: formulas.filter(f => f.status === 2).length,
        partial: formulas.filter(f => f.status === 1).length,
        notStarted: formulas.filter(f => !f.status || f.status === 0).length
    };

    if (loading) {
        return (
            <main className="student-content">
                <div className="loading">Загрузка...</div>
            </main>
        );
    }

    return (
        <main className="student-content">
            <div className="container">
            <div className="page-header">
                <h1>Речевые формулы</h1>
                <p>Ваш прогресс изучения речевых формул</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                <div className="card" style={{ textAlign: 'center', padding: '15px' }}>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#4a90e2' }}>
                        {stats.total}
                    </div>
                    <div style={{ fontSize: '14px', color: '#7f8c8d' }}>Всего формул</div>
                </div>
                <div className="card" style={{ textAlign: 'center', padding: '15px' }}>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#52c41a' }}>
                        {stats.mastered}
                    </div>
                    <div style={{ fontSize: '14px', color: '#7f8c8d', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                        <FiCheckCircle /> Усвоено
                    </div>
                </div>
                <div className="card" style={{ textAlign: 'center', padding: '15px' }}>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#faad14' }}>
                        {stats.partial}
                    </div>
                    <div style={{ fontSize: '14px', color: '#7f8c8d', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                        <FiAlertCircle /> Требует повторения
                    </div>
                </div>
                <div className="card" style={{ textAlign: 'center', padding: '15px' }}>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#d9d9d9' }}>
                        {stats.notStarted}
                    </div>
                    <div style={{ fontSize: '14px', color: '#7f8c8d', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                        <FiCircle /> Не начато
                    </div>
                </div>
            </div>

            <div className="card" style={{ marginBottom: '20px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Фильтр:</label>
                    <select 
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        style={{ width: '100%', maxWidth: '300px' }}
                    >
                        <option value="all">Все формулы ({stats.total})</option>
                        <option value="mastered">Усвоено ({stats.mastered})</option>
                        <option value="partial">Требует повторения ({stats.partial})</option>
                        <option value="not-started">Не начато ({stats.notStarted})</option>
                    </select>
                </div>
            </div>

            {filteredFormulas.length === 0 ? (
                <div className="card">
                    <p style={{ textAlign: 'center', color: '#999' }}>
                        {filter === 'all' 
                            ? 'Речевые формулы еще не добавлены преподавателем' 
                            : 'Нет формул с выбранным статусом'
                        }
                    </p>
                </div>
            ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                    {filteredFormulas.map(formula => {
                        const statusInfo = getStatusInfo(formula.status);
                        return (
                            <div key={formula.id} className="card" style={{ padding: '20px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px' }}>
                                    <div style={{ flex: 1 }}>
                                        {formula.formula_ru ? (
                                            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                                                {formula.formula_ru}
                                            </div>
                                        ) : (
                                            <div style={{ fontSize: '14px', color: '#999', fontStyle: 'italic', marginBottom: '8px' }}>
                                                Перевод не указан
                                            </div>
                                        )}
                                        {formula.context && (
                                            <div style={{ fontSize: '13px', color: '#999', marginTop: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '4px', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                                <FiInfo style={{ marginTop: '2px', flexShrink: 0 }} /> {formula.context}
                                            </div>
                                        )}
                                        <div style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
                                            {formula.level_name && `${formula.level_name}`}
                                            {formula.lesson_title && ` | ${formula.lesson_title}`}
                                        </div>
                                    </div>
                                    <div style={{ 
                                        padding: '8px 16px', 
                                        borderRadius: '20px', 
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        background: `${statusInfo.color}20`,
                                        color: statusInfo.color,
                                        border: `1px solid ${statusInfo.color}`,
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {statusInfo.icon} {statusInfo.text}
                                    </div>
                                </div>
                                {formula.notes && (
                                    <div style={{ 
                                        marginTop: '12px', 
                                        padding: '10px', 
                                        background: '#fffbe6', 
                                        borderLeft: '3px solid #faad14',
                                        fontSize: '13px'
                                    }}>
                                        <strong>Заметка преподавателя:</strong> {formula.notes}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    </main>
    );
};

/** Прослушивание для фонетического диктанта: загруженный файл или TTS с сервера */
const PhoneticDictationListenButton = ({ question, disabled }) => {
    const [loading, setLoading] = React.useState(false);
    const [playing, setPlaying] = React.useState(false);
    const audioRef = React.useRef(null);

    const stop = React.useCallback(() => {
        const a = audioRef.current;
        if (a) {
            a.pause();
            a.currentTime = 0;
            const src = a.src;
            if (src && src.startsWith('blob:')) {
                URL.revokeObjectURL(src);
            }
            audioRef.current = null;
        }
        setPlaying(false);
        setLoading(false);
    }, []);

    React.useEffect(() => () => stop(), [stop]);

    const questionData = typeof question.question_data === 'string'
        ? (() => { try { return JSON.parse(question.question_data); } catch (e) { return {}; } })()
        : (question.question_data || {});
    const audioType = questionData.audioType || (questionData.audioUrl ? 'custom' : 'auto');

    const play = async () => {
        if (disabled || loading) return;
        stop();
        setLoading(true);
        try {
            const res = await studentAPI.getPhoneticDictationAudio(question.id);
            const blob = res.data;
            const ct = (res.headers && res.headers['content-type']) || '';
            if (!blob || blob.size === 0 || (ct && !ct.includes('audio') && !ct.includes('octet-stream'))) {
                throw new Error('empty');
            }
            const url = URL.createObjectURL(blob);
            const a = new Audio(url);
            audioRef.current = a;
            a.onended = () => {
                URL.revokeObjectURL(url);
                setPlaying(false);
                audioRef.current = null;
            };
            a.onerror = () => {
                URL.revokeObjectURL(url);
                setPlaying(false);
                audioRef.current = null;
            };
            setLoading(false);
            setPlaying(true);
            await a.play();
        } catch (e) {
            console.error(e);
            setLoading(false);
            setPlaying(false);
            alert('Не удалось воспроизвести аудио. Если выбрана авто-озвучка, на сервере должен быть настроен Edge TTS.');
        }
    };

    if (audioType === 'custom' && questionData.audioUrl) {
        return (
            <div style={{ marginBottom: '12px' }}>
                <audio
                    controls
                    src={`${API_ORIGIN}${questionData.audioUrl}`}
                    style={{ width: '100%' }}
                />
            </div>
        );
    }

    return (
        <div style={{ marginBottom: '12px' }}>
            <button
                type="button"
                onClick={playing ? stop : play}
                disabled={disabled || loading}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 16px',
                    background: playing ? '#ff4d4f' : '#4a90e2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: disabled || loading ? 'not-allowed' : 'pointer',
                    opacity: disabled || loading ? 0.7 : 1
                }}
            >
                {loading ? 'Загрузка…' : playing ? <><FiSquare size={16} /> Стоп</> : <><FiPlay size={16} /> Прослушать (озвучка)</>}
            </button>
        </div>
    );
};

/** Аудио/озвучка инструкции для любого типа вопроса */
const QuestionPromptAudio = ({ questionData, disabled }) => {
    const [playing, setPlaying] = React.useState(false);

    const promptAudioType = questionData?.promptAudioType || 'none';
    const promptAudioUrl = questionData?.promptAudioUrl || '';
    const promptTtsText = (questionData?.promptTtsText || '').trim();
    const promptVoiceId = questionData?.promptVoiceId || 'en-US-JennyNeural';

    React.useEffect(() => () => {
        ttsService.cancel();
    }, []);

    if (promptAudioType === 'none') return null;

    if (promptAudioType === 'custom' && promptAudioUrl) {
        return (
            <div style={{ marginBottom: '12px' }}>
                <audio controls src={`${API_ORIGIN}${promptAudioUrl}`} style={{ width: '100%' }} />
            </div>
        );
    }

    if (promptAudioType === 'tts' && promptTtsText) {
        return (
            <div style={{ marginBottom: '12px' }}>
                <button
                    type="button"
                    onClick={() => {
                        if (playing) {
                            ttsService.cancel();
                            setPlaying(false);
                            return;
                        }
                        setPlaying(true);
                        ttsService.speak(promptTtsText, {
                            rate: 1,
                            lang: 'en-US',
                            voice: promptVoiceId
                        }).then(() => setPlaying(false)).catch(() => setPlaying(false));
                    }}
                    disabled={disabled}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        background: playing ? '#ff4d4f' : '#4a90e2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.7 : 1
                    }}
                >
                    {playing ? <><FiSquare size={16} /> Стоп аудио</> : <><FiPlay size={16} /> Прослушать задание</>}
                </button>
            </div>
        );
    }

    return null;
};

/** Краткая подпись прогресса по сохранённой попытке (для списка упражнений и шапки блока) */
function formatStudentExerciseProgressLabel(exerciseType, att) {
    if (!att) return null;
    const st = att.status || 'saved';
    if (exerciseType === 'text_answer') {
        if (st === 'checked') {
            if (att.score != null && att.score !== '') {
                return `Оценка преподавателя: ${att.score} из 100`;
            }
            return 'Проверено преподавателем';
        }
        if (st === 'submitted') return 'На проверке у преподавателя';
        if (st === 'needs_revision') return 'Нужна доработка';
        return null;
    }
    if (st === 'saved' && (att.percentage != null || att.total != null)) {
        const pct = att.percentage != null ? att.percentage : 0;
        const tot = att.total != null ? att.total : '?';
        const sc = att.score != null ? att.score : 0;
        return `Автопроверка: ${pct}% (${sc}/${tot})`;
    }
    return null;
}

// Компонент для отображения одного упражнения (блочный урок — постраничный просмотр)
const ExerciseItem = ({ exercise, exIdx, lessonId, initialAttempt, onRefreshAttempts }) => {
    const [exerciseData, setExerciseData] = React.useState(null);
    const [answers, setAnswers] = React.useState({});
    const [results, setResults] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    
    // Загружаем данные упражнения
    React.useEffect(() => {
        const loadExercise = async () => {
            try {
                const response = await studentAPI.getExercise(exercise.id);
                setExerciseData(response.data);
                setLoading(false);
            } catch (err) {
                console.error('Error loading exercise:', err);
                setLoading(false);
            }
        };
        loadExercise();
    }, [exercise.id]);

    // Восстанавливаем сохранённые ответы / результат после обновления страницы
    React.useEffect(() => {
        if (!exerciseData) return;
        if (!initialAttempt) {
            setAnswers({});
            setResults(null);
            return;
        }
        setAnswers(initialAttempt.answers || {});
        const ext = exerciseData.exercise?.exercise_type;
        const st = initialAttempt.status || 'saved';
        if (ext === 'text_answer') {
            if (st === 'submitted' || st === 'checked' || st === 'needs_revision') {
                setResults({
                    manualReview: true,
                    status: st,
                    message: st === 'checked'
                        ? 'Преподаватель проверил работу.'
                        : st === 'needs_revision'
                            ? 'Требуется доработка. Исправьте ответ и отправьте снова на проверку.'
                            : 'Ответ отправлен преподавателю на проверку.',
                    teacherComment: initialAttempt.teacher_comment || '',
                    teacherScore: initialAttempt.score != null ? initialAttempt.score : undefined
                });
            } else {
                setResults(null);
            }
            return;
        }
        setResults({
            score: initialAttempt.score,
            total: initialAttempt.total,
            percentage: initialAttempt.percentage,
            results: []
        });
    }, [exercise.id, exerciseData, initialAttempt]);

    const isTextAnswer = exerciseData?.exercise?.exercise_type === 'text_answer';
    
    const handleAnswerChange = (questionId, answer) => {
        setAnswers({
            ...answers,
            [questionId]: answer
        });
    };
    
    const submitTextAnswerForReview = async () => {
        if (!lessonId || !exerciseData) return;
        const qList = exerciseData.questions || [];
        const unanswered = qList.filter((q) => {
            const v = answers[q.id];
            return v == null || String(v).trim() === '';
        });
        if (unanswered.length > 0) {
            alert(`Ответьте на все вопросы (осталось ${unanswered.length})`);
            return;
        }
        try {
            await studentAPI.submitExercise(exercise.id, {
                lesson_id: lessonId,
                answers,
                score: 0,
                total: qList.length,
                percentage: null
            });
            setResults({
                manualReview: true,
                status: 'submitted',
                message: 'Работа отправлена преподавателю на проверку.'
            });
            if (onRefreshAttempts) await onRefreshAttempts();
        } catch (e) {
            console.error(e);
            const msg = e.response?.data?.error || 'Не удалось отправить на проверку';
            alert(msg);
        }
    };

    const handleSubmit = async () => {
        if (exerciseData?.exercise?.exercise_type === 'text_answer') {
            return;
        }
        if (exerciseData?.exercise?.exercise_type === 'phonetic_dictation') {
            const unanswered = questions.filter((q) => {
                const v = answers[q.id];
                return v === undefined || v === null || String(v).trim() === '';
            });
            if (unanswered.length > 0) {
                alert(`Ответьте на все вопросы диктанта. Осталось: ${unanswered.length}`);
                return;
            }
        }
        try {
            const response = await studentAPI.checkExercise({
                exerciseId: exercise.id,
                answers
            });
            setResults(response.data);
            if (lessonId) {
                try {
                    await studentAPI.saveExerciseAttempt(exercise.id, {
                        lesson_id: lessonId,
                        answers,
                        score: response.data.score,
                        total: response.data.total,
                        percentage: response.data.percentage
                    });
                    if (onRefreshAttempts) await onRefreshAttempts();
                } catch (e) {
                    console.error('Не удалось сохранить результат:', e);
                }
            }
        } catch (err) {
            console.error('Error checking exercise:', err);
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                Загрузка упражнения...
            </div>
        );
    }
    
    if (!exerciseData || !exerciseData.questions) {
        return null;
    }
    
    const questions = exerciseData.questions;
    const exercisePromptData = questions.length > 0
        ? (typeof questions[0].question_data === 'string'
            ? (() => { try { return JSON.parse(questions[0].question_data); } catch (e) { return {}; } })()
            : (questions[0].question_data || {}))
        : {};
    const inputsReadOnly = isTextAnswer
        ? !!(results?.manualReview && results.status !== 'needs_revision')
        : !!results;
    const progressLabel = formatStudentExerciseProgressLabel(
        exerciseData.exercise.exercise_type,
        initialAttempt
    );
    
    return (
        <div style={{
            padding: '30px',
            borderBottom: '2px solid #f0f0f0'
        }}>
            {/* Заголовок упражнения */}
            <div style={{
                marginBottom: '25px',
                padding: '20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '8px'
            }}>
                <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: 'white',
                    margin: '0 0 8px 0'
                }}>
                    {exerciseData.exercise.title || `Упражнение ${exIdx + 1}`}
                </h3>
                {exerciseData.exercise.instructions && (
                    <p style={{
                        fontSize: '14px',
                        color: 'rgba(255,255,255,0.9)',
                        margin: 0
                    }}>
                        {exerciseData.exercise.instructions}
                    </p>
                )}
                {progressLabel && (
                    <p style={{
                        fontSize: '14px',
                        color: 'rgba(255,255,255,0.95)',
                        margin: '10px 0 0 0',
                        fontWeight: 600
                    }}>
                        {progressLabel}
                    </p>
                )}
            </div>

            {exerciseData.exercise.exercise_type !== 'phonetic_dictation' && (
                <QuestionPromptAudio questionData={exercisePromptData} disabled={inputsReadOnly} />
            )}
            
            {/* Вопросы */}
            <div style={{ marginBottom: '20px' }}>
                {questions.map((question, qIdx) => {
                    const questionData = typeof question.question_data === 'string' 
                        ? JSON.parse(question.question_data) 
                        : question.question_data || {};
                    
                    const questionResult = results?.results?.find(r => r.questionId === question.id);
                    
                    return (
                        <div key={qIdx} style={{
                            marginBottom: '25px',
                            padding: '20px',
                            background: questionResult ? 
                                (questionResult.isCorrect ? '#f6ffed' : '#fff2f0') : 
                                '#f8f9fa',
                            borderRadius: '8px',
                            border: questionResult ? 
                                (questionResult.isCorrect ? '2px solid #52c41a' : '2px solid #ff4d4f') : 
                                '1px solid #e8e8e8'
                        }}>
                            {/* Номер и текст вопроса */}
                            <div style={{ marginBottom: '15px' }}>
                                <span style={{
                                    display: 'inline-block',
                                    background: '#667eea',
                                    color: 'white',
                                    padding: '4px 12px',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    marginBottom: '10px'
                                }}>
                                    Вопрос {qIdx + 1}
                                </span>
                                <p style={{ 
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    margin: '8px 0 0 0',
                                    color: '#2c3e50'
                                }}>
                                    {question.question_text}
                                </p>
                            </div>
                            
                            {/* Рендеринг полей для ответов */}
                            <div style={{ marginTop: '15px' }}>
                                {/* Multiple Choice */}
                                {exerciseData.exercise.exercise_type === 'multiple_choice' && questionData.options && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {questionData.options.map((option, optIdx) => (
                                            <label
                                                key={optIdx}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '12px 16px',
                                                    background: answers[question.id] === optIdx ? '#e3f2fd' : 'white',
                                                    border: answers[question.id] === optIdx ? '2px solid #4a90e2' : '2px solid #e0e0e0',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    checked={answers[question.id] === optIdx}
                                                    onChange={() => handleAnswerChange(question.id, optIdx)}
                                                    style={{ marginRight: '12px' }}
                                                    disabled={inputsReadOnly}
                                                />
                                                <span>{option}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                                
                                {/* True/False */}
                                {exerciseData.exercise.exercise_type === 'true_false' && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '12px 16px',
                                            background: answers[question.id] === true ? '#e8f5e9' : 'white',
                                            border: answers[question.id] === true ? '2px solid #52c41a' : '2px solid #e0e0e0',
                                            borderRadius: '8px',
                                            cursor: 'pointer'
                                        }}>
                                            <input
                                                type="radio"
                                                    checked={String(answers[question.id]) === 'true'}
                                                    onChange={() => handleAnswerChange(question.id, 'true')}
                                                style={{ marginRight: '12px' }}
                                                disabled={inputsReadOnly}
                                            />
                                            <span>✓ Правда (True)</span>
                                        </label>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '12px 16px',
                                            background: answers[question.id] === false ? '#ffebee' : 'white',
                                            border: answers[question.id] === false ? '2px solid #ff4d4f' : '2px solid #e0e0e0',
                                            borderRadius: '8px',
                                            cursor: 'pointer'
                                        }}>
                                            <input
                                                type="radio"
                                                    checked={String(answers[question.id]) === 'false'}
                                                    onChange={() => handleAnswerChange(question.id, 'false')}
                                                style={{ marginRight: '12px' }}
                                                disabled={inputsReadOnly}
                                            />
                                            <span>✗ Ложь (False)</span>
                                        </label>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '12px 16px',
                                            background: answers[question.id] === 'not_stated' ? '#fff7e6' : 'white',
                                            border: answers[question.id] === 'not_stated' ? '2px solid #fa8c16' : '2px solid #e0e0e0',
                                            borderRadius: '8px',
                                            cursor: 'pointer'
                                        }}>
                                            <input
                                                type="radio"
                                                checked={answers[question.id] === 'not_stated'}
                                                onChange={() => handleAnswerChange(question.id, 'not_stated')}
                                                style={{ marginRight: '12px' }}
                                                disabled={inputsReadOnly}
                                            />
                                            <span>~ Не указано (Not stated)</span>
                                        </label>
                                    </div>
                                )}
                                
                                {/* Fill in the gaps */}
                                {exerciseData.exercise.exercise_type === 'fill_gaps' && questionData.sentence && (
                                    <div>
                                        <p style={{ fontSize: '15px', marginBottom: '15px', lineHeight: '1.6' }}>
                                            {questionData.sentence}
                                        </p>
                                        {Array.from({ length: questionData.sentence.split('___').length - 1 }).map((_, gapIdx) => (
                                            <div key={gapIdx} style={{ marginBottom: '10px' }}>
                                                <label style={{ fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block' }}>
                                                    Пропуск {gapIdx + 1}:
                                                </label>
                                                <input
                                                    type="text"
                                                    value={(answers[question.id] || [])[gapIdx] || ''}
                                                    onChange={(e) => {
                                                        const newAnswers = [...(answers[question.id] || [])];
                                                        newAnswers[gapIdx] = e.target.value;
                                                        handleAnswerChange(question.id, newAnswers);
                                                    }}
                                                    disabled={inputsReadOnly}
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px',
                                                        border: '2px solid #e0e0e0',
                                                        borderRadius: '8px',
                                                        fontSize: '15px'
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {/* Match */}
                                {exerciseData.exercise.exercise_type === 'match' && questionData.pairs && (
                                    <div>
                                        {questionData.pairs.map((pair, pairIdx) => (
                                            <div key={pairIdx} style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr auto 1fr',
                                                gap: '15px',
                                                marginBottom: '15px'
                                            }}>
                                                <div style={{
                                                    padding: '12px',
                                                    background: '#f0f7ff',
                                                    borderRadius: '8px',
                                                    textAlign: 'center'
                                                }}>
                                                    {pair.left}
                                                </div>
                                                <div>⟷</div>
                                                <select
                                                    value={(answers[question.id] || {})[pairIdx] || ''}
                                                    onChange={(e) => {
                                                        const newAnswers = answers[question.id] || {};
                                                        handleAnswerChange(question.id, { ...newAnswers, [pairIdx]: e.target.value });
                                                    }}
                                                    disabled={inputsReadOnly}
                                                    style={{
                                                        padding: '12px',
                                                        border: '2px solid #e0e0e0',
                                                        borderRadius: '8px',
                                                        width: '100%'
                                                    }}
                                                >
                                                    <option value="">Выберите...</option>
                                                    {questionData.pairs.map((p, i) => (
                                                        <option key={i} value={p.right}>{p.right}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {/* Arrange */}
                                {exerciseData.exercise.exercise_type === 'arrange' && questionData.words && (
                                    <div>
                                        <div style={{
                                            minHeight: '60px',
                                            padding: '15px',
                                            background: '#f8f9fa',
                                            border: '2px dashed #d0e7ff',
                                            borderRadius: '8px',
                                            marginBottom: '15px',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '8px'
                                        }}>
                                            {(answers[question.id] || []).length > 0 ? (
                                                (answers[question.id] || []).map((word, idx) => (
                                                    <div
                                                        key={idx}
                                                        onClick={() => {
                                                            if (!inputsReadOnly) {
                                                                const newAnswer = [...(answers[question.id] || [])];
                                                                newAnswer.splice(idx, 1);
                                                                handleAnswerChange(question.id, newAnswer);
                                                            }
                                                        }}
                                                        style={{
                                                            padding: '8px 16px',
                                                            background: '#4a90e2',
                                                            color: 'white',
                                                            borderRadius: '20px',
                                                            cursor: inputsReadOnly ? 'default' : 'pointer'
                                                        }}
                                                    >
                                                        {word}
                                                    </div>
                                                ))
                                            ) : (
                                                <span style={{ color: '#999' }}>Нажмите на слова ниже</span>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                            {questionData.words
                                                .sort(() => Math.random() - 0.5)
                                                .filter(word => {
                                                    const wordCount = questionData.words.filter(w => w === word).length;
                                                    const usedCount = (answers[question.id] || []).filter(w => w === word).length;
                                                    return usedCount < wordCount;
                                                })
                                                .map((word, idx) => (
                                                    <div
                                                        key={idx}
                                                        onClick={() => {
                                                            if (!inputsReadOnly) {
                                                                handleAnswerChange(question.id, [...(answers[question.id] || []), word]);
                                                            }
                                                        }}
                                                        style={{
                                                            padding: '8px 16px',
                                                            background: '#f0f7ff',
                                                            border: '2px solid #d0e7ff',
                                                            borderRadius: '20px',
                                                            cursor: inputsReadOnly ? 'default' : 'pointer'
                                                        }}
                                                    >
                                                        {word}
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}

                                {exerciseData.exercise.exercise_type === 'text_answer' && (
                                    <div>
                                        <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '6px' }}>
                                            Ваш ответ:
                                        </label>
                                        <textarea
                                            value={answers[question.id] || ''}
                                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                            disabled={inputsReadOnly}
                                            rows={5}
                                            placeholder="Введите развёрнутый ответ…"
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '8px',
                                                fontSize: '15px',
                                                resize: 'vertical',
                                                fontFamily: 'inherit'
                                            }}
                                        />
                                        <p style={{ fontSize: '12px', color: '#888', margin: '8px 0 0 0' }}>
                                            Ответ проверяет преподаватель. Нажмите «Отправить на проверку», когда будете готовы.
                                        </p>
                                    </div>
                                )}

                                {exerciseData.exercise.exercise_type === 'phonetic_dictation' && (
                                    <div>
                                        <PhoneticDictationListenButton question={question} disabled={inputsReadOnly} />
                                        <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '6px' }}>
                                            Напишите услышанное по-английски:
                                        </label>
                                        <textarea
                                            value={answers[question.id] || ''}
                                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                            disabled={inputsReadOnly}
                                            rows={4}
                                            placeholder="Введите предложение…"
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '8px',
                                                fontSize: '15px',
                                                resize: 'vertical',
                                                fontFamily: 'inherit'
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            
                            {/* Результат вопроса */}
                            {questionResult && (
                                <div style={{
                                    marginTop: '15px',
                                    padding: '10px',
                                    background: questionResult.isCorrect ? '#d9f7be' : '#ffccc7',
                                    borderRadius: '4px',
                                    fontSize: '13px'
                                }}>
                                    {questionResult.isCorrect ? '✓ Правильно!' : '✗ Неправильно'}
                                    {!questionResult.isCorrect && exerciseData.exercise.exercise_type === 'phonetic_dictation' && questionResult.correctAnswer != null && (
                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#333' }}>
                                            <strong>Верный ответ:</strong> {String(questionResult.correctAnswer)}
                                        </div>
                                    )}
                                    {questionResult.explanation && (
                                        <div style={{ marginTop: '5px', fontSize: '12px' }}>
                                            {questionResult.explanation}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            
            {isTextAnswer && results?.manualReview && (
                <div style={{
                    padding: '20px',
                    background: '#e6f7ff',
                    border: '2px solid #1890ff',
                    borderRadius: '8px',
                    marginTop: '12px',
                    marginBottom: '16px'
                }}>
                    <p style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#2c3e50' }}>{results.message}</p>
                    {results.status === 'checked' && results.teacherScore != null && (
                        <p style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#2c3e50', fontWeight: 600 }}>
                            Баллы: {results.teacherScore} из 100
                        </p>
                    )}
                    {results.teacherComment ? (
                        <div style={{
                            marginTop: '8px',
                            padding: '12px',
                            background: 'white',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#333'
                        }}>
                            <strong>Комментарий преподавателя:</strong>
                            <p style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>{results.teacherComment}</p>
                        </div>
                    ) : null}
                </div>
            )}

            {/* Кнопки: автопроверка или текстовый ответ */}
            {!results && !isTextAnswer && (
                <button
                    onClick={handleSubmit}
                    style={{
                        width: '100%',
                        padding: '15px',
                        background: '#52c41a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    Проверить ответы
                </button>
            )}
            {isTextAnswer && (!results || results.status === 'needs_revision') && (
                <button
                    type="button"
                    onClick={submitTextAnswerForReview}
                    style={{
                        width: '100%',
                        padding: '15px',
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    {results?.status === 'needs_revision'
                        ? 'Отправить исправленный ответ на проверку'
                        : 'Отправить на проверку преподавателю'}
                </button>
            )}
            
            {/* Результаты автопроверки */}
            {results && !results.manualReview && (
                <div style={{
                    padding: '20px',
                    background: results.percentage >= 70 ? '#f6ffed' : '#fff9e6',
                    border: `2px solid ${results.percentage >= 70 ? '#52c41a' : '#faad14'}`,
                    borderRadius: '8px',
                    marginTop: '20px'
                }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#2c3e50' }}>
                        Результат: {results.score} из {results.total} ({results.percentage}%)
                    </h4>
                    <button
                        onClick={() => {
                            setAnswers({});
                            setResults(null);
                        }}
                        style={{
                            padding: '10px 20px',
                            background: '#4a90e2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            marginTop: '10px'
                        }}
                    >
                        Попробовать снова
                    </button>
                </div>
            )}
        </div>
    );
};

// Страница просмотра "урока-материала" (материал со ссылкой на `lesson_id`)
const StudentMaterialLessonView = () => {
    const { materialId } = useParams();
    const [material, setMaterial] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const loadMaterial = async () => {
            try {
                setLoading(true);
                const res = await materialsAPI.getStudentMaterial(materialId);
                setMaterial(res.data || null);
            } catch (e) {
                console.error('Ошибка загрузки материала:', e);
                alert('Не удалось загрузить материал');
            } finally {
                setLoading(false);
            }
        };

        if (materialId) loadMaterial();
    }, [materialId]);

    if (loading) {
        return <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Загрузка...</div>;
    }

    if (!material || !material.lesson_id) {
        return <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Материал не найден</div>;
    }

    return (
        <StudentLessonView
            lessonIdOverride={material.lesson_id}
            backTo="/student/materials"
        />
    );
};

const StudentLessonView = ({ lessonIdOverride = null, backTo = null } = {}) => {
    const params = useParams();
    const lessonRef = lessonIdOverride ?? params.lessonId;
    const navigate = useNavigate();
    const location = window.location;
    const getLessonRequestParams = () => {
        const searchParams = new URLSearchParams(location.search);
        const levelIdParam = searchParams.get('levelId');
        const requestParams = {};
        if (levelIdParam) requestParams.levelId = levelIdParam;
        if (backTo) requestParams.includeMaterial = 1;
        return requestParams;
    };
    const [lesson, setLesson] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [currentBlockIndex, setCurrentBlockIndex] = React.useState(0);
    const [showSentenceTranslations, setShowSentenceTranslations] = React.useState({});
    const [hoveredSentence, setHoveredSentence] = React.useState(null);
    const [hoveredPart, setHoveredPart] = React.useState(null);
    const [playbackSpeed, setPlaybackSpeed] = React.useState(1);
    const [activeSection, setActiveSection] = React.useState('sentences');
    const [expandedSidebarLessons, setExpandedSidebarLessons] = React.useState({});
    const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
    
    // Автоматически раскрываем текущий урок в сайдбаре при загрузке
    React.useEffect(() => {
        if (lesson?.id && lesson.hasBlocks && lesson.blocks && lesson.blocks.length > 0) {
            setExpandedSidebarLessons(prev => ({
                ...prev,
                [lesson.id]: true
            }));
        }
    }, [lesson]);
    
    // Считываем параметр block из URL
    React.useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const blockParam = searchParams.get('block');
        if (blockParam !== null) {
            setCurrentBlockIndex(parseInt(blockParam, 10));
        }
    }, [location.search]);
    const [audioPlayers, setAudioPlayers] = React.useState({});
    const [openExercise, setOpenExercise] = React.useState(null);
    const [exerciseAnswers, setExerciseAnswers] = React.useState({});
    const [exerciseResults, setExerciseResults] = React.useState(null);
    const [lessonExerciseAttempts, setLessonExerciseAttempts] = React.useState(null); // сохранённые результаты по упражнениям урока
    
    // Состояние для бокового меню
    const [sidebarCategory, setSidebarCategory] = React.useState('grammar');
    const [sidebarAreas, setSidebarAreas] = React.useState([]);
    const [sidebarSelectedArea, setSidebarSelectedArea] = React.useState(null);
    const [sidebarLessons, setSidebarLessons] = React.useState([]);
    const activeSidebarLessonRef = React.useRef(null);
    const activeSidebarBlockRef = React.useRef(null);
    
    // Состояние для выделения предложений в тексте
    const [selectedTextSentence, setSelectedTextSentence] = React.useState(null);
    const [showTextTranslation, setShowTextTranslation] = React.useState(null);
    
    // Состояние для записи аудио
    const [isRecording, setIsRecording] = React.useState(false);
    const [recordingSentence, setRecordingSentence] = React.useState(null);
    const [mediaRecorder, setMediaRecorder] = React.useState(null);
    const [audioChunks, setAudioChunks] = React.useState([]);
    const [savedRecordings, setSavedRecordings] = React.useState({}); // { sentenceId: { url, blob, isPlaying } }
    const [playingRecordingId, setPlayingRecordingId] = React.useState(null);
    const [recordingPlaybackLoadingId, setRecordingPlaybackLoadingId] = React.useState(null);
    const playingRecordingRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const recordingLimitTimerRef = useRef(null);
    const recordingStartedAtRef = useRef(null);

    const clearRecordingLimitTimer = () => {
        if (recordingLimitTimerRef.current) {
            clearTimeout(recordingLimitTimerRef.current);
            recordingLimitTimerRef.current = null;
        }
    };

    React.useEffect(() => () => clearRecordingLimitTimer(), []);

    // Состояние для озвучки предложений
    const [currentAudio, setCurrentAudio] = React.useState(null);
    const [isAudioPlaying, setIsAudioPlaying] = React.useState(false);
    const [isAudioPaused, setIsAudioPaused] = React.useState(false);
    const [playingSentenceKey, setPlayingSentenceKey] = React.useState(null);

    const effectiveLessonId = lesson?.id ?? (lessonIdOverride || null);

    React.useEffect(() => {
        loadLesson();
        if (!backTo) {
            loadSidebarData();
        }
    }, [lessonRef, location.search]);

    React.useEffect(() => {
        const activeItem = activeSidebarBlockRef.current || activeSidebarLessonRef.current;
        if (!activeItem) return;

        const frameId = window.requestAnimationFrame(() => {
            activeItem.scrollIntoView({ block: 'center', behavior: 'smooth' });
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [lessonRef, sidebarLessons, expandedSidebarLessons, currentBlockIndex, activeSection, lesson, backTo]);

    // Авто‑подсветка пункта меню при скролле страницы
    React.useEffect(() => {
        if (!lesson) return;

        const handleScroll = () => {
            const sections = [];

            // Проверяем наличие блоков
            if (lesson.hasBlocks && lesson.blocks) {
                // Для блочной структуры
                lesson.blocks.forEach((block, idx) => {
                    const el = document.getElementById(`block-${idx}`);
                    if (el) sections.push({ id: `block-${idx}`, top: el.getBoundingClientRect().top });
                });
            } else {
                // Для старой структуры
                const hasSent = lesson.standaloneSentences && lesson.standaloneSentences.length > 0;
                const hasTxt = lesson.texts && lesson.texts.length > 0;
                const hasVid = lesson.videos && lesson.videos.length > 0;

                if (hasSent) {
                    const el = document.getElementById('sentences');
                    if (el) sections.push({ id: 'sentences', top: el.getBoundingClientRect().top });
                }

                if (hasTxt && lesson.texts) {
                    lesson.texts.forEach((_, idx) => {
                        const el = document.getElementById(`text-${idx}`);
                        if (el) sections.push({ id: `text-${idx}`, top: el.getBoundingClientRect().top });
                    });
                }

                if (hasVid) {
                    const el = document.getElementById('videos');
                    if (el) sections.push({ id: 'videos', top: el.getBoundingClientRect().top });
                }
            }

            if (!sections.length) return;

            // Находим секцию, ближайшую к верхней границе (с небольшим отступом)
            const offset = 120;
            let current = sections[0].id;
            let minDistance = Infinity;

            sections.forEach(section => {
                const distance = Math.abs(section.top - offset);
                if (distance < minDistance) {
                    minDistance = distance;
                    current = section.id;
                }
            });

            setActiveSection(current);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lesson]);
    
    const loadSidebarData = async () => {
        try {
            // Загружаем текущий урок чтобы узнать категорию
            const lessonRes = await studentAPI.getLesson(lessonRef, getLessonRequestParams());
            const currentLesson = lessonRes.data;
            const category = currentLesson.category || 'grammar';
            setSidebarCategory(category);
            
            // Загружаем области для этой категории
            const areasRes = await studentAPI.getAreas({ category });
            const areas = areasRes.data.areas || [];
            setSidebarAreas(areas);
            
            // Если есть уровень у текущего урока, находим область и загружаем уроки
            if (currentLesson.level_id && areas.length > 0) {
                const levelsPromises = areas.map(area => 
                    studentAPI.getLevels(area.id).then(res => ({
                        areaId: area.id,
                        levels: res.data.levels || []
                    }))
                );
                const areasWithLevels = await Promise.all(levelsPromises);
                
                // Находим область текущего урока
                let foundArea = null;
                for (const areaData of areasWithLevels) {
                    if (areaData.levels.some(level => level.id === currentLesson.level_id)) {
                        foundArea = areas.find(a => a.id === areaData.areaId);
                        break;
                    }
                }
                
                if (foundArea) {
                    setSidebarSelectedArea(foundArea.id);
                    // Загружаем все уроки этой области
                    const levelsRes = await studentAPI.getLevels(foundArea.id);
                    const levels = levelsRes.data.levels || [];
                    const lessonsPromises = levels.map(level =>
                        studentAPI.getLessons(level.id).then(res => res.data.lessons || [])
                    );
                    const allLessonsArrays = await Promise.all(lessonsPromises);
                    const allLessons = allLessonsArrays.flat().filter(l => l.category === category);
                    setSidebarLessons(allLessons);
                }
            }
        } catch (error) {
            console.error('Ошибка загрузки данных бокового меню:', error);
        }
    };

    const loadLesson = async () => {
        try {
            setLoading(true);
            const response = await studentAPI.getLesson(lessonRef, getLessonRequestParams());
            const loadedLesson = response.data;
            setLesson(loadedLesson);
            
            // Загружаем существующие аудиозаписи для этого урока
            await loadRecordings(loadedLesson.id);

            // Загружаем сохранённые результаты упражнений (чтобы не слетало после обновления страницы)
            try {
                const attRes = await studentAPI.getLessonExerciseAttempts(loadedLesson.id);
                setLessonExerciseAttempts(attRes.data.attempts || {});
            } catch (e) {
                setLessonExerciseAttempts({});
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Ошибка загрузки урока:', error);
            setLoading(false);
        }
    };

    const refreshExerciseAttempts = React.useCallback(async () => {
        if (!effectiveLessonId) return;
        try {
            const attRes = await studentAPI.getLessonExerciseAttempts(effectiveLessonId);
            setLessonExerciseAttempts(attRes.data.attempts || {});
        } catch (e) {
            /* ignore */
        }
    }, [effectiveLessonId]);
    
    const loadRecordings = async (dbLessonId = effectiveLessonId) => {
        if (!dbLessonId) return;
        try {
            const response = await studentAPI.getAudioRecordings({ lessonId: dbLessonId });
            const recordings = response.data.recordings || [];
            
            // Самая новая запись на предложение или на блок целиком (b-{block_id})
            const recordingsMap = {};
            recordings.forEach((rec) => {
                const mapKey =
                    rec.block_id != null
                        ? `b-${rec.block_id}`
                        : rec.sentence_id != null
                          ? String(rec.sentence_id)
                          : null;
                if (!mapKey || recordingsMap[mapKey]) return;
                recordingsMap[mapKey] = {
                    id: rec.id,
                    url: `${API_ORIGIN}${rec.audio_url}`,
                    createdAt: rec.created_at,
                    uploaded: true,
                    localUrl: null,
                    blob: null,
                    feedbackRating: rec.feedback_rating || null,
                    feedbackComment: rec.feedback_comment || null,
                    feedbackCommentAudioUrl: rec.feedback_comment_audio_url
                        ? `${API_ORIGIN}${rec.feedback_comment_audio_url}`
                        : null,
                    feedbackCheckedAt: rec.feedback_checked_at || null,
                };
            });
            setSavedRecordings(recordingsMap);
        } catch (error) {
            console.error('Ошибка загрузки записей:', error);
        }
    };

    const playSentence = (text, voiceId = 'en-US-JennyNeural', audioUrl = null, sentenceKey = null) => {
        stopRecordingPlayback();

        // Если есть загруженное аудио, используем его
        if (audioUrl) {
            // Останавливаем предыдущее аудио если есть
            if (currentAudio) {
                currentAudio.pause();
                currentAudio.currentTime = 0;
            }
            
            const audio = new Audio(`${API_ORIGIN}${audioUrl}`);
            audio.playbackRate = playbackSpeed;
            
            audio.onplay = () => {
                setIsAudioPlaying(true);
                setIsAudioPaused(false);
                setPlayingSentenceKey(sentenceKey);
            };
            
            audio.onpause = () => {
                setIsAudioPlaying(false);
                setIsAudioPaused(true);
            };
            
            audio.onended = () => {
                setIsAudioPlaying(false);
                setIsAudioPaused(false);
                setPlayingSentenceKey(null);
                setCurrentAudio(null);
            };
            
            setCurrentAudio(audio);
            audio.play().catch(error => {
                console.error('Ошибка воспроизведения загруженного аудио:', error);
                setIsAudioPlaying(false);
                setIsAudioPaused(false);
                setPlayingSentenceKey(null);
            });
        } else {
            // Останавливаем предыдущее аудио если есть
            if (currentAudio) {
                currentAudio.pause();
                currentAudio.currentTime = 0;
                setCurrentAudio(null);
            }
            
            // Иначе используем TTS
            // Убираем разделители | из текста для воспроизведения
            const cleanText = text.replace(/\s*\|\s*/g, ' ').trim();
            
            setIsAudioPlaying(true);
            setIsAudioPaused(false);
            setPlayingSentenceKey(sentenceKey);
            
            ttsService.speak(cleanText, {
                rate: playbackSpeed,
                lang: 'en-US',
                voice: voiceId,
                onEnd: () => {
                    setIsAudioPlaying(false);
                    setIsAudioPaused(false);
                    setPlayingSentenceKey(null);
                }
            }).catch(error => {
                console.error('Ошибка воспроизведения:', error);
                setIsAudioPlaying(false);
                setIsAudioPaused(false);
                setPlayingSentenceKey(null);
            });
        }
    };
    
    const pauseAudio = () => {
        if (currentAudio && isAudioPlaying) {
            currentAudio.pause();
            setIsAudioPlaying(false);
            setIsAudioPaused(true);
        } else {
            ttsService.pause();
            setIsAudioPlaying(false);
            setIsAudioPaused(true);
        }
    };
    
    const resumeAudio = () => {
        if (currentAudio && isAudioPaused) {
            currentAudio.play();
            setIsAudioPlaying(true);
            setIsAudioPaused(false);
        } else {
            ttsService.resume();
            setIsAudioPlaying(true);
            setIsAudioPaused(false);
        }
    };
    
    const stopAudio = () => {
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            setCurrentAudio(null);
        }
        ttsService.stop();
        setIsAudioPlaying(false);
        setIsAudioPaused(false);
        setPlayingSentenceKey(null);
    };

    const renderTextBlockRecording = (block, textIdx) => {
        const blockId = block?.id;
        if (!blockId) return null;

        const rid = `b-${blockId}`;
        const recordingKey = `text-block-rec-${textIdx}`;
        const uploaded = savedRecordings[rid]?.uploaded === true;
        const isActive = isRecording && recordingSentence === recordingKey;

        return (
            <div style={{ marginTop: '28px', paddingTop: '22px', borderTop: '2px dashed #e8e8e8' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#2c3e50', marginBottom: '8px' }}>
                    Запишите текст целиком
                </div>
                <p style={{ fontSize: '13px', color: '#666', margin: '0 0 14px 0', lineHeight: 1.5 }}>
                    Произнесите весь текст одной записью и отправьте преподавателю на проверку.
                </p>
                <button
                    type="button"
                    onClick={() => {
                        if (isActive) stopRecording();
                        else startRecording(rid, recordingKey);
                    }}
                    disabled={uploaded}
                    style={{
                        background: isActive ? '#f5222d' : uploaded ? '#95de64' : '#52c41a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 18px',
                        cursor: uploaded ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: 600,
                        opacity: uploaded ? 0.7 : 1,
                    }}
                >
                    {uploaded ? <FiCheck size={16} /> : isActive ? <FiSquare size={16} /> : <FiMic size={16} />}
                    {uploaded
                        ? 'Запись отправлена'
                        : isActive
                          ? 'Остановить запись'
                          : 'Начать запись'}
                </button>
                {renderRecordingStatusCard(rid)}
            </div>
        );
    };

    const stopRecordingPlayback = () => {
        if (playingRecordingRef.current) {
            playingRecordingRef.current.pause();
            playingRecordingRef.current.currentTime = 0;
            playingRecordingRef.current = null;
        }
        setPlayingRecordingId(null);
        setRecordingPlaybackLoadingId(null);
    };

    const renderRecordingPlayButton = (rid) => {
        const sid = rid != null ? String(rid) : '';
        if (!sid) return null;
        const isLoading = recordingPlaybackLoadingId === sid;
        const isPlaying = playingRecordingId === sid;

        return (
            <button
                type="button"
                className={`recording-status-btn recording-play-btn${isPlaying ? ' recording-play-btn--active' : ''}${isLoading ? ' recording-play-btn--loading' : ''}`}
                onClick={() => playRecording(rid)}
                disabled={isLoading}
                title={isPlaying ? 'Остановить воспроизведение' : 'Прослушать свою запись'}
            >
                {isLoading ? (
                    <>
                        <span className="recording-play-spinner" aria-hidden="true" />
                        Загрузка…
                    </>
                ) : isPlaying ? (
                    <>
                        <span className="recording-play-waves" aria-hidden="true">
                            <span /><span /><span />
                        </span>
                        Воспроизведение…
                    </>
                ) : (
                    <>
                        <FiPlay size={16} />
                        Прослушать
                    </>
                )}
            </button>
        );
    };

    const renderRecordingStatusCard = (rid) => {
        if (!rid || !savedRecordings[rid]) return null;
        const rec = savedRecordings[rid];
        const hasDraft = !!(rec.blob || rec.localUrl);
        const rerecordAwaitingNewTake = !rec.uploaded && rec.priorSubmissionAudioUrl && !hasDraft;

        return (
            <div
                className="recording-status-card"
                style={{
                    marginTop: '10px',
                    padding: '12px 15px',
                    background: rec.uploaded ? '#f6ffed' : '#e6f7ff',
                    border: rec.uploaded ? '1px solid #b7eb8f' : '1px solid #91d5ff',
                    borderRadius: '8px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '12px',
                }}
            >
                {rec.uploaded ? (
                    <>
                        <FiCheck style={{ color: '#52c41a', fontSize: '22px', flexShrink: 0 }} />
                        <span style={{ flex: '1 1 200px', color: '#52c41a', fontSize: '14px', fontWeight: '600' }}>
                            Запись отправлена преподавателю
                        </span>
                        {renderRecordingPlayButton(rid)}
                        <button type="button" className="recording-status-btn" onClick={() => startRerecordSentence(rid)} style={{ padding: '8px 16px', background: '#fff', color: '#389e0d', border: '1px solid #b7eb8f', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}>
                            <FiRefreshCw size={16} /> Записать повторно
                        </button>
                    </>
                ) : rerecordAwaitingNewTake ? (
                    <>
                        <FiMic style={{ color: '#1890ff', fontSize: '22px', flexShrink: 0 }} />
                        <span style={{ flex: '1 1 200px', color: '#1890ff', fontSize: '14px', fontWeight: '600' }}>
                            Сделайте новую запись для проверки
                        </span>
                        <button type="button" className="recording-status-btn" onClick={() => deleteRecording(rid)} style={{ padding: '8px 16px', background: 'transparent', color: '#ff4d4f', border: '1px solid #ff4d4f', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <FiTrash2 /> Отменить
                        </button>
                    </>
                ) : (
                    <>
                        <FiMic style={{ color: '#1890ff', fontSize: '22px', flexShrink: 0 }} />
                        <span style={{ flex: '1 1 200px', color: '#1890ff', fontSize: '14px', fontWeight: '600' }}>
                            {rec.priorSubmissionAudioUrl ? 'Новая запись • Не отправлено' : 'Запись готова • Не отправлено'}
                        </span>
                        {renderRecordingPlayButton(rid)}
                        <button type="button" className="recording-status-btn" onClick={() => uploadRecording(rid)} style={{ padding: '8px 20px', background: '#52c41a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}>
                            <FiUpload /> Отправить преподавателю
                        </button>
                        <button type="button" onClick={() => deleteRecording(rid)} style={{ padding: '8px 16px', background: 'transparent', color: '#ff4d4f', border: '1px solid #ff4d4f', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <FiTrash2 /> Удалить
                        </button>
                    </>
                )}
            </div>
        );
    };

    const startRecording = async (sentenceId, sentenceKey) => {
        const sid = sentenceId != null ? String(sentenceId) : null;
        if (!sid) {
            alert('Не удалось определить предложение для записи');
            return;
        }
        try {
            stopRecordingPlayback();
            clearRecordingLimitTimer();
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            const chunks = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                }
            };

            recorder.onstop = async () => {
                clearRecordingLimitTimer();
                setIsRecording(false);
                setRecordingSentence(null);
                mediaRecorderRef.current = null;

                const startedAt = recordingStartedAtRef.current;
                recordingStartedAtRef.current = null;
                const durationSeconds =
                    startedAt != null ? Math.max(0, Math.round((Date.now() - startedAt) / 1000)) : null;

                const audioBlob = new Blob(chunks, { type: 'audio/webm' });
                const localUrl = URL.createObjectURL(audioBlob);
                setSavedRecordings((prev) => ({
                    ...prev,
                    [sid]: {
                        ...prev[sid],
                        blob: audioBlob,
                        localUrl,
                        uploaded: false,
                        durationSeconds,
                    },
                }));

                stream.getTracks().forEach((track) => track.stop());
            };

            recorder.start(1000);
            mediaRecorderRef.current = recorder;
            recordingStartedAtRef.current = Date.now();
            recordingLimitTimerRef.current = setTimeout(() => {
                const r = mediaRecorderRef.current;
                if (r && r.state === 'recording') {
                    r.stop();
                    clearRecordingLimitTimer();
                    setTimeout(() => {
                        const limitLabel =
                            SPEECH_RECORDING_MAX_SECONDS >= 60 &&
                            SPEECH_RECORDING_MAX_SECONDS % 60 === 0
                                ? `${SPEECH_RECORDING_MAX_SECONDS / 60} мин`
                                : `${SPEECH_RECORDING_MAX_SECONDS} с`;
                        alert(`Достигнут лимит записи (${limitLabel}). Запись остановлена.`);
                    }, 0);
                } else {
                    clearRecordingLimitTimer();
                }
            }, SPEECH_RECORDING_MAX_SECONDS * 1000);

            setMediaRecorder(recorder);
            setIsRecording(true);
            setRecordingSentence(sentenceKey);
        } catch (error) {
            console.error('Ошибка доступа к микрофону:', error);
            alert('Не удалось получить доступ к микрофону');
        }
    };

    const stopRecording = () => {
        clearRecordingLimitTimer();
        const r = mediaRecorderRef.current || mediaRecorder;
        if (r && isRecording && r.state === 'recording') {
            r.stop();
            setIsRecording(false);
            setRecordingSentence(null);
        }
    };

    /** Новая запись на проверку поверх уже отправленной */
    const startRerecordSentence = (sentenceId) => {
        const sid = sentenceId != null ? String(sentenceId) : null;
        if (!sid) return;

        clearRecordingLimitTimer();
        if (mediaRecorder && isRecording) {
            try {
                mediaRecorder.stream?.getTracks().forEach((t) => t.stop());
            } catch (e) {
                /* ignore */
            }
            try {
                mediaRecorder.stop();
            } catch (e) {
                /* ignore */
            }
            setIsRecording(false);
            setRecordingSentence(null);
            setMediaRecorder(null);
            mediaRecorderRef.current = null;
            recordingStartedAtRef.current = null;
        }

        stopRecordingPlayback();

        setSavedRecordings((prev) => {
            const c = prev[sid];
            if (!c?.uploaded) return prev;
            const hasFb = c.feedbackRating || c.feedbackComment || c.feedbackCommentAudioUrl;
            return {
                ...prev,
                [sid]: {
                    uploaded: false,
                    blob: null,
                    localUrl: null,
                    priorSubmissionAudioUrl: c.url,
                    priorTeacherFeedback: hasFb
                        ? {
                              rating: c.feedbackRating,
                              comment: c.feedbackComment,
                              commentAudioUrl: c.feedbackCommentAudioUrl,
                              checkedAt: c.feedbackCheckedAt,
                          }
                        : null,
                    feedbackRating: null,
                    feedbackComment: null,
                    feedbackCommentAudioUrl: null,
                    feedbackCheckedAt: null,
                },
            };
        });
    };

    const uploadRecording = async (sentenceId) => {
        const sid = sentenceId != null ? String(sentenceId) : null;
        if (!sid) return;
        const recording = savedRecordings[sid];
        if (!recording || !recording.blob) return;
        
        try {
            const formData = new FormData();
            formData.append('audio', recording.blob, 'recording.webm');
            const blockMatch = /^b-(\d+)$/.exec(sid);
            if (blockMatch) {
                formData.append('blockId', blockMatch[1]);
            } else {
                formData.append('sentenceId', sid);
            }
            formData.append('lessonId', effectiveLessonId || lessonRef);
            if (recording.durationSeconds != null) {
                formData.append('duration', String(recording.durationSeconds));
            }
            
            const response = await studentAPI.uploadAudio(formData);
            
            // Освобождаем локальный URL перед обновлением
            if (recording.localUrl) {
                URL.revokeObjectURL(recording.localUrl);
            }
            
            // Обновляем статус записи с серверным URL
            setSavedRecordings((prev) => ({
                ...prev,
                [sid]: {
                    uploaded: true,
                    id: response.data.recordingId,
                    url: `${API_ORIGIN}${response.data.audioUrl}`,
                    localUrl: null,
                    blob: null,
                    priorSubmissionAudioUrl: undefined,
                    priorTeacherFeedback: undefined,
                    feedbackRating: null,
                    feedbackComment: null,
                    feedbackCommentAudioUrl: null,
                    feedbackCheckedAt: null,
                },
            }));

            alert('Запись успешно отправлена!');
        } catch (error) {
            console.error('Ошибка при отправке записи:', error);
            const serverMsg = error.response?.data?.error;
            alert(serverMsg || 'Ошибка при отправке записи');
        }
    };
    
    const playRecording = (sentenceId) => {
        const sid = sentenceId != null ? String(sentenceId) : null;
        if (!sid) return;
        const recording = savedRecordings[sid];
        if (!recording) return;

        if (playingRecordingId === sid) {
            stopRecordingPlayback();
            return;
        }

        stopRecordingPlayback();
        stopAudio();

        const audioUrl = recording.uploaded ? recording.url : recording.localUrl;

        if (!audioUrl) {
            console.error('URL аудио не найден:', recording);
            alert('Не удалось найти файл записи');
            return;
        }

        setRecordingPlaybackLoadingId(sid);

        const audio = new Audio(audioUrl);
        playingRecordingRef.current = audio;

        audio.onplaying = () => {
            setRecordingPlaybackLoadingId(null);
            setPlayingRecordingId(sid);
        };

        audio.onended = () => {
            stopRecordingPlayback();
        };

        audio.onerror = () => {
            stopRecordingPlayback();
            alert('Не удалось воспроизвести запись');
        };

        audio.play().catch((err) => {
            console.error('Ошибка воспроизведения:', err);
            stopRecordingPlayback();
            alert('Не удалось воспроизвести запись');
        });
    };
    
    const deleteRecording = (sentenceId) => {
        const sid = sentenceId != null ? String(sentenceId) : null;
        if (!sid) return;

        stopRecordingPlayback();

        const recording = savedRecordings[sid];
        if (!recording) return;

        if (recording.localUrl) {
            URL.revokeObjectURL(recording.localUrl);
        }

        // Отмена повторной записи — подтянуть с сервера последнюю отправленную версию
        if (!recording.uploaded && recording.priorSubmissionAudioUrl) {
            loadRecordings();
            return;
        }

        setSavedRecordings((prev) => {
            const next = { ...prev };
            delete next[sid];
            return next;
        });
    };

    const toggleSentenceTranslation = (key) => {
        setShowSentenceTranslations(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const scrollToSection = (sectionId) => {
        setActiveSection(sectionId);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const parseSentenceWithParts = (sentenceEn, sentenceRu) => {
        const splitToLines = (text) => {
            if (text == null || text === '') return [];
            const s = String(text);
            if (s.includes('|')) {
                return s.split('|').map((p) => p.trim()).filter(Boolean);
            }
            if (/\r?\n/.test(s)) {
                return s.split(/\r?\n/).map((p) => p.trim()).filter(Boolean);
            }
            return [s.trim()].filter(Boolean);
        };
        const partsEn = splitToLines(sentenceEn);
        const partsRu = splitToLines(sentenceRu);
        return {
            en: partsEn.length > 0 ? partsEn : sentenceEn != null && sentenceEn !== '' ? [String(sentenceEn)] : [],
            ru: partsRu.length > 0 ? partsRu : sentenceRu != null && sentenceRu !== '' ? [String(sentenceRu)] : [],
        };
    };

    const renderSentenceWithParts = (sentence, index, prefix) => {
        const sentenceKey = `${prefix}-${index}`;
        const rid =
            sentence.recordingKey != null
                ? String(sentence.recordingKey)
                : sentence.id != null
                  ? String(sentence.id)
                  : null;
        const showTranslation = showSentenceTranslations[sentenceKey];
        const parts = parseSentenceWithParts(sentence.sentence_en, sentence.sentence_ru);

        return (
            <div 
                key={sentenceKey}
                style={{ 
                    padding: '15px 20px',
                    background: 'white',
                    borderBottom: '1px solid #f0f0f0',
                    position: 'relative',
                    transition: 'all 0.2s'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                    <span style={{ 
                        color: '#999', 
                        fontSize: '14px',
                        minWidth: '30px',
                        fontWeight: '500'
                    }}>
                        {sentence.rowLabel != null ? sentence.rowLabel : index + 1}
                    </span>
                    
                    {/* Кнопка воспроизведения/паузы */}
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                        <button 
                            onClick={() => {
                                if (playingSentenceKey === sentenceKey && isAudioPlaying) {
                                    pauseAudio();
                                } else if (playingSentenceKey === sentenceKey && isAudioPaused) {
                                    resumeAudio();
                                } else {
                                    playSentence(sentence.sentence_en, sentence.voice_id, sentence.audio_url, sentenceKey);
                                }
                            }}
                            style={{
                                background: playingSentenceKey === sentenceKey ? '#52c41a' : '#4a90e2',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '32px',
                                height: '32px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s'
                            }}
                            title={playingSentenceKey === sentenceKey && isAudioPlaying ? 'Пауза' : 'Прослушать'}
                        >
                            {playingSentenceKey === sentenceKey && isAudioPlaying ? <FiPause size={14} /> : <FiPlay size={14} />}
                        </button>
                        
                        {playingSentenceKey === sentenceKey && (isAudioPlaying || isAudioPaused) && (
                            <button 
                                onClick={stopAudio}
                                style={{
                                    background: '#ff4d4f',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '32px',
                                    height: '32px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s'
                                }}
                                title="Остановить"
                            >
                                <FiSquare size={14} />
                            </button>
                        )}
                    </div>
                    
                    {/* Иконка глаза для переключения перевода - сразу после воспроизведения */}
                    {sentence.sentence_ru && (
                        <button
                            onClick={() => toggleSentenceTranslation(sentenceKey)}
                            style={{
                                background: 'transparent',
                                border: '1px solid',
                                borderColor: showTranslation ? '#52c41a' : '#ccc',
                                borderRadius: '50%',
                                cursor: 'pointer',
                                fontSize: '16px',
                                color: showTranslation ? '#52c41a' : '#999',
                                width: '32px',
                                height: '32px',
                                padding: 0,
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                            title={showTranslation ? 'Скрыть перевод' : 'Показать перевод'}
                        >
                            <FiEye size={16} />
                        </button>
                    )}
                    
                    <button 
                        onClick={() => {
                            if (!rid) return;
                            if (isRecording && recordingSentence === sentenceKey) {
                                stopRecording();
                            } else {
                                startRecording(rid, sentenceKey);
                            }
                        }}
                        disabled={!rid || savedRecordings[rid]?.uploaded === true}
                        style={{
                            background: isRecording && recordingSentence === sentenceKey 
                                ? '#f5222d' 
                                : rid && savedRecordings[rid]?.uploaded === true
                                ? '#95de64' 
                                : '#52c41a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            cursor: !rid || savedRecordings[rid]?.uploaded === true ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s',
                            opacity: !rid || savedRecordings[rid]?.uploaded === true ? 0.6 : 1
                        }}
                        title={
                            !rid
                                ? 'Нет id предложения'
                                : savedRecordings[rid]?.uploaded === true
                                ? "Запись отправлена преподавателю" 
                                : isRecording && recordingSentence === sentenceKey 
                                ? "Остановить запись" 
                                : "Записать своё произношение"
                        }
                    >
                        {rid && savedRecordings[rid]?.uploaded === true ? (
                            <FiCheck />
                        ) : isRecording && recordingSentence === sentenceKey ? (
                            <FiSquare />
                        ) : (
                            <FiMic />
                        )}
                    </button>
                    
                    <div 
                        style={{ flex: 1, position: 'relative', lineHeight: '2' }}
                    >
                        {/* Показываем текст с переносами строк вместо | */}
                        <div style={{ 
                            fontSize: '16px', 
                            lineHeight: 1.65,
                            color: '#2c3e50'
                        }}>
                            {parts.ru.map((line, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        marginBottom: idx < parts.ru.length - 1 ? '0.5em' : 0,
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {line}
                                </div>
                            ))}
                        </div>

                        {/* Перевод - если включен переключатель */}
                        {showTranslation && sentence.sentence_en && (
                            <div style={{ 
                                marginTop: '8px',
                                fontSize: '14px',
                                color: '#52c41a',
                                lineHeight: 1.65,
                            }}>
                                {parts.en.map((line, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            marginBottom: idx < parts.en.length - 1 ? '0.5em' : 0,
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {line}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Блок управления записью - появляется после записи */}
                {rid && savedRecordings[rid] && (() => {
                    const rec = savedRecordings[rid];
                    const hasDraft = !!(rec.blob || rec.localUrl);
                    const rerecordAwaitingNewTake = !rec.uploaded && rec.priorSubmissionAudioUrl && !hasDraft;

                    return (
                    <div
                        className="recording-status-card"
                        style={{
                        marginTop: '12px',
                        marginLeft: '45px',
                        padding: '12px 15px',
                        background: rec.uploaded ? '#f6ffed' : '#e6f7ff',
                        border: rec.uploaded ? '1px solid #b7eb8f' : '1px solid #91d5ff',
                        borderRadius: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '12px',
                        animation: 'fadeIn 0.3s ease'
                    }}>
                        {rec.uploaded ? (
                            <>
                                <FiCheck style={{ color: '#52c41a', fontSize: '22px', flexShrink: 0 }} />
                                <span className="recording-status-text" style={{ 
                                    flex: '1 1 200px', 
                                    color: '#52c41a',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    Запись отправлена преподавателю
                                </span>
                                {renderRecordingPlayButton(rid)}
                                <button
                                    type="button"
                                    className="recording-status-btn"
                                    onClick={() => startRerecordSentence(rid)}
                                    style={{
                                        padding: '8px 16px',
                                        background: '#fff',
                                        color: '#389e0d',
                                        border: '1px solid #b7eb8f',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        fontWeight: '600',
                                        flexShrink: 0,
                                    }}
                                >
                                    <FiRefreshCw size={16} /> Записать повторно
                                </button>
                            </>
                        ) : rerecordAwaitingNewTake ? (
                            <>
                                <FiMic style={{ color: '#1890ff', fontSize: '22px', flexShrink: 0 }} />
                                <span className="recording-status-text" style={{ 
                                    flex: '1 1 200px', 
                                    color: '#1890ff',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    lineHeight: 1.45
                                }}>
                                    Сделайте новую запись для проверки. Кнопка «Начать запись» или зелёный микрофон слева.
                                </span>
                                <button
                                    type="button"
                                    className="recording-status-btn"
                                    onClick={() => startRecording(rid, sentenceKey)}
                                    disabled={isRecording && recordingSentence === sentenceKey}
                                    style={{
                                        padding: '8px 18px',
                                        background: '#52c41a',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: isRecording && recordingSentence === sentenceKey ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        fontWeight: '600',
                                        flexShrink: 0,
                                        opacity: isRecording && recordingSentence === sentenceKey ? 0.7 : 1,
                                    }}
                                >
                                    <FiMic size={16} /> Начать запись
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => deleteRecording(rid)}
                                    style={{
                                        padding: '8px 16px',
                                        background: 'transparent',
                                        color: '#ff4d4f',
                                        border: '1px solid #ff4d4f',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: 'all 0.2s',
                                        fontWeight: '500',
                                        flexShrink: 0
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = '#ff4d4f';
                                        e.target.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'transparent';
                                        e.target.style.color = '#ff4d4f';
                                    }}
                                >
                                    <FiTrash2 /> Отменить
                                </button>
                                {rec.priorSubmissionAudioUrl && (
                                    <div
                                        style={{
                                            width: '100%',
                                            flexBasis: '100%',
                                            marginTop: '4px',
                                            paddingTop: '12px',
                                            borderTop: '1px dashed #91d5ff',
                                        }}
                                    >
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                                            Ранее отправленная версия (ещё доступна преподавателю)
                                        </div>
                                        <audio
                                            controls
                                            style={{ width: '100%', maxWidth: '420px', height: '36px' }}
                                            src={rec.priorSubmissionAudioUrl}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <FiMic style={{ color: '#1890ff', fontSize: '22px', flexShrink: 0 }} />
                                <span className="recording-status-text" style={{ 
                                    flex: '1 1 200px', 
                                    color: '#1890ff',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    {rec.priorSubmissionAudioUrl
                                        ? 'Новая запись • Отправьте преподавателю'
                                        : 'Запись готова • Не отправлено'}
                                </span>
                                {renderRecordingPlayButton(rid)}
                                <button
                                    className="recording-status-btn"
                                    onClick={() => uploadRecording(rid)}
                                    style={{
                                        padding: '8px 20px',
                                        background: '#52c41a',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: 'all 0.2s',
                                        fontWeight: '600',
                                        flexShrink: 0
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#429a0d'}
                                    onMouseLeave={(e) => e.target.style.background = '#52c41a'}
                                >
                                    <FiUpload /> Отправить преподавателю
                                </button>
                                <button 
                                    onClick={() => deleteRecording(rid)}
                                    style={{
                                        padding: '8px 16px',
                                        background: 'transparent',
                                        color: '#ff4d4f',
                                        border: '1px solid #ff4d4f',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: 'all 0.2s',
                                        fontWeight: '500',
                                        flexShrink: 0
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = '#ff4d4f';
                                        e.target.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'transparent';
                                        e.target.style.color = '#ff4d4f';
                                    }}
                                >
                                    <FiTrash2 />{' '}
                                    {rec.priorSubmissionAudioUrl
                                        ? 'Отменить'
                                        : 'Удалить'}
                                </button>
                                {rec.priorSubmissionAudioUrl && (
                                    <div
                                        style={{
                                            width: '100%',
                                            flexBasis: '100%',
                                            marginTop: '4px',
                                            paddingTop: '12px',
                                            borderTop: '1px dashed #91d5ff',
                                        }}
                                    >
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                                            Ранее отправленная версия (ещё доступна преподавателю)
                                        </div>
                                        <audio
                                            controls
                                            style={{ width: '100%', maxWidth: '420px', height: '36px' }}
                                            src={rec.priorSubmissionAudioUrl}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    );
                })()}
                {(() => {
                    const rec = rid ? savedRecordings[rid] : null;
                    if (!rec) return null;
                    const teacherReply = rec.uploaded
                        ? {
                              rating: rec.feedbackRating,
                              comment: rec.feedbackComment,
                              commentAudioUrl: rec.feedbackCommentAudioUrl,
                              checkedAt: rec.feedbackCheckedAt,
                          }
                        : rec.priorTeacherFeedback;
                    if (
                        !teacherReply ||
                        (!teacherReply.rating &&
                            !teacherReply.comment &&
                            !teacherReply.commentAudioUrl)
                    ) {
                        return null;
                    }
                    return (
                        <div
                            style={{
                                marginTop: '12px',
                                marginLeft: '45px',
                                padding: '14px 16px',
                                background: '#fffbe6',
                                border: '1px solid #ffe58f',
                                borderRadius: '8px',
                            }}
                        >
                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#ad6800', marginBottom: '10px' }}>
                                Ответ преподавателя
                            </div>
                            {!rec.uploaded && rec.priorTeacherFeedback && (
                                <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#8c8c8c', lineHeight: 1.4 }}>
                                    К предыдущей отправленной записи. После проверки новой версии здесь появится актуальный
                                    комментарий.
                                </p>
                            )}
                            {teacherReply.rating && (
                                <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                                    <span style={{ color: '#666' }}>Оценка: </span>
                                    <strong>
                                        {(
                                            {
                                                excellent: 'Отлично',
                                                good: 'Хорошо',
                                                needs_improvement: 'Нужно улучшить',
                                                poor: 'Плохо',
                                            }[teacherReply.rating] || teacherReply.rating
                                        )}
                                    </strong>
                                </div>
                            )}
                            {teacherReply.comment && (
                                <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333', lineHeight: 1.5 }}>
                                    {teacherReply.comment}
                                </p>
                            )}
                            {teacherReply.commentAudioUrl && (
                                <div>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                                        Голосовой комментарий
                                    </div>
                                    <audio
                                        controls
                                        style={{ width: '100%', maxWidth: '420px', height: '36px' }}
                                        src={teacherReply.commentAudioUrl}
                                    />
                                </div>
                            )}
                            {teacherReply.checkedAt && (
                                <div style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
                                    {new Date(teacherReply.checkedAt).toLocaleString('ru-RU')}
                                </div>
                            )}
                        </div>
                    );
                })()}
            </div>
        );
    };

    /** Перенос строки: перевод строки в тексте, слэш с любыми пробелами (в т.ч. NBSP), не режем URL с :// */
    const splitTextIntoDisplayLines = (value) => {
        if (value == null || value === '') return [];
        const norm = (s) => String(s).replace(/\u00A0/g, ' ').replace(/\r\n/g, '\n');
        const out = [];
        for (const line of norm(value).split('\n')) {
            let t = line.trim();
            if (!t) continue;
            t = norm(t);
            if (t.includes('://')) {
                out.push(t);
                continue;
            }
            const parts = t.split(/\s*[\u002F\uFF0F]\s*/).map((p) => p.trim()).filter(Boolean);
            if (parts.length > 1) {
                parts.forEach((p) => out.push(p));
            } else {
                out.push(t);
            }
        }
        return out;
    };

    const renderTextWithLineBreaks = (value) => {
        if (value == null || value === '') return null;
        const str = String(value);
        const segments = splitTextIntoDisplayLines(str);
        if (segments.length <= 1) return str;
        return (
            <span style={{ display: 'inline-block', verticalAlign: 'top', maxWidth: '100%' }}>
                {segments.map((seg, i) => (
                    <span key={i} style={{ display: 'block', lineHeight: 'inherit' }}>
                        {seg}
                    </span>
                ))}
            </span>
        );
    };

    const textForTts = (s) => String(s == null ? '' : s).replace(/\s*[\u002F\uFF0F]\s*/g, ' ').replace(/\s+/g, ' ').trim();

    const renderFullText = (text, textIdx) => {
        // Используем sentences из БД если доступны, иначе парсим строку
        const sentencesDataRaw = text.sentences && text.sentences.length > 0 
            ? text.sentences 
            : null;
        const sentencesData = sentencesDataRaw ? expandSentenceRows(sentencesDataRaw) : null;
        
        const sentences = sentencesData 
            ? sentencesData.map(s => s.sentence_en)
            : (text.full_text_en ? parseSentencesFromFullText(text.full_text_en) : []);
            
        const translationSentences = sentencesData
            ? sentencesData.map(s => s.sentence_ru)
            : (text.full_text_ru ? parseSentencesFromFullText(text.full_text_ru) : []);

        /** Перенос строки между двумя соседними фрагментами, если в исходнике между ними был слэш или перевод строки */
        const shouldLineBreakBetweenInRaw = (rawFull, prevSent, nextSent) => {
            if (rawFull == null || rawFull === '') return false;
            const full = String(rawFull).replace(/\u00A0/g, ' ');
            const a = String(prevSent ?? '').trim();
            const b = String(nextSent ?? '').trim();
            if (!a || !b) return false;
            let searchFrom = 0;
            while (searchFrom <= full.length) {
                const ia = full.indexOf(a, searchFrom);
                if (ia === -1) return false;
                const afterA = full.slice(ia + a.length);
                const ib = afterA.indexOf(b);
                if (ib !== -1) {
                    const between = afterA.slice(0, ib);
                    return /[\u002F\uFF0F]/.test(between) || /\r|\n/.test(between);
                }
                searchFrom = ia + 1;
            }
            return false;
        };

        const lineBreakBeforeSentence = (idx) => {
            if (idx <= 0) return false;
            if (
                sentencesData &&
                sentencesData[idx - 1]?.id != null &&
                sentencesData[idx - 1].id === sentencesData[idx].id
            ) {
                return true;
            }
            if (shouldLineBreakBetweenInRaw(text.full_text_en, sentences[idx - 1], sentences[idx])) {
                return true;
            }
            const trPrev = translationSentences[idx - 1];
            const trNext = translationSentences[idx];
            if (trPrev != null && trNext != null && shouldLineBreakBetweenInRaw(text.full_text_ru, trPrev, trNext)) {
                return true;
            }
            return false;
        };

        return (
            <div style={{ lineHeight: '2.2', fontSize: '16px' }}>
                {sentences.map((sent, idx) => {
                    const sentKey = `full-text-${textIdx}-${idx}`;
                    const translation = translationSentences[idx];
                    
                    // Получаем voice_id для этого предложения
                    const voiceId = sentencesData && sentencesData[idx] 
                        ? sentencesData[idx].voice_id 
                        : (text.voice_id || 'en-US-JennyNeural');
                    // Получаем audio_url для этого предложения
                    const audioUrl = sentencesData && sentencesData[idx] 
                        ? sentencesData[idx].audio_url 
                        : null;
                    
                    // Проверяем, есть ли разделение на части
                    const hasParts = sent.includes('|');
                    
                    if (hasParts) {
                        // Если есть части - разбиваем и рендерим каждую часть отдельно
                        const sentParts = sent.split('|').map(p => p.trim());
                        const translationParts = translation ? translation.split('|').map(p => p.trim()) : [];
                        
                        // Получаем аудио для частей
                        const sentenceData = sentencesData ? sentencesData[idx] : null;
                        let partsAudio = [];
                        if (sentenceData && sentenceData.parts_audio) {
                            try {
                                const partsAudioArray = typeof sentenceData.parts_audio === 'string' 
                                    ? JSON.parse(sentenceData.parts_audio) 
                                    : sentenceData.parts_audio;
                                partsAudio = partsAudioArray || [];
                            } catch (e) {
                                console.error('Ошибка парсинга parts_audio:', e);
                            }
                        }
                        
                        return (
                            <React.Fragment key={idx}>
                                {lineBreakBeforeSentence(idx) && <br />}
                                {sentParts.map((part, pIdx) => {
                                    const partKey = `${sentKey}-part-${pIdx}`;
                                    const isHovered = hoveredSentence === partKey;
                                    const isSelected = selectedTextSentence === partKey;
                                    const mainText = translationParts[pIdx] || part;
                                    const tooltipText = translationParts[pIdx] ? part : null;
                                    const partAudio = partsAudio[pIdx] || null;
                                    
                                    return (
                                        <span
                                            key={pIdx}
                                            style={{
                                                display: 'inline-block',
                                                position: 'relative',
                                                marginLeft: isSelected ? '40px' : '0',
                                                marginRight: isSelected ? '40px' : '0',
                                                transition: 'margin 0.2s ease'
                                            }}
                                        >
                                            <span
                                                onMouseEnter={() => setHoveredSentence(partKey)}
                                                onMouseLeave={() => setHoveredSentence(null)}
                                                onClick={() => {
                                                    setSelectedTextSentence(isSelected ? null : partKey);
                                                    setShowTextTranslation(null);
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    display: 'inline',
                                                    background: isSelected ? 'rgba(74, 144, 226, 0.15)' : isHovered ? 'rgba(74, 144, 226, 0.05)' : 'transparent',
                                                    padding: '2px 4px',
                                                    borderRadius: '4px',
                                                    position: 'relative',
                                                    opacity: selectedTextSentence && !isSelected ? 0.4 : 1
                                                }}
                                            >
                                                {/* Иконка воспроизведения слева */}
                                                {isSelected && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            playSentence(textForTts(part), voiceId, partAudio);
                                                        }}
                                                        style={{
                                                            position: 'absolute',
                                                            left: '-35px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            background: '#4a90e2',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '50%',
                                                            width: '28px',
                                                            height: '28px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '12px',
                                                            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'}
                                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(-50%) scale(1)'}
                                                    >
                                                        ▶
                                                    </button>
                                                )}
                                                {renderTextWithLineBreaks(mainText)}
                                                
                                                {/* Иконка глазика справа */}
                                                {isSelected && tooltipText && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowTextTranslation(showTextTranslation === partKey ? null : partKey);
                                                        }}
                                                        style={{
                                                            position: 'absolute',
                                                            right: '-35px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            background: '#52c41a',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '50%',
                                                            width: '28px',
                                                            height: '28px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '14px',
                                                            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'}
                                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(-50%) scale(1)'}
                                                    >
                                                        <FiEye />
                                                    </button>
                                                )}
                                                
                                                {/* Tooltip — английский перевод */}
                                                {showTextTranslation === partKey && tooltipText && (
                                                    <span style={{
                                                        position: 'absolute',
                                                        bottom: '100%',
                                                        left: '50%',
                                                        transform: 'translateX(-50%)',
                                                        marginBottom: '8px',
                                                        padding: '8px 12px',
                                                        background: '#2c3e50',
                                                        color: 'white',
                                                        borderRadius: '8px',
                                                        fontSize: '13px',
                                                        lineHeight: '1.5',
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                        zIndex: 1000,
                                                        animation: 'fadeIn 0.2s ease',
                                                        fontWeight: '400',
                                                        maxWidth: '350px',
                                                        minWidth: '150px',
                                                        whiteSpace: 'normal',
                                                        textAlign: 'center'
                                                    }}>
                                                        {renderTextWithLineBreaks(tooltipText)}
                                                        <span style={{
                                                            content: '',
                                                            position: 'absolute',
                                                            top: '100%',
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            width: 0,
                                                            height: 0,
                                                            borderLeft: '6px solid transparent',
                                                            borderRight: '6px solid transparent',
                                                            borderTop: '6px solid #2c3e50'
                                                        }} />
                                                    </span>
                                                )}
                                            </span>
                                            {pIdx < sentParts.length - 1 && ' '}
                                        </span>
                                    );
                                })}
                            </React.Fragment>
                        );
                    } else {
                        // Обычное предложение без частей
                        const isHovered = hoveredSentence === sentKey;
                        const isSelected = selectedTextSentence === sentKey;
                        const mainText = translation ? translation.trim() : sent.trim();
                        const tooltipText = translation ? sent.trim() : null;

                        return (
                            <React.Fragment key={idx}>
                                {lineBreakBeforeSentence(idx) && <br />}
                                <span
                                    style={{
                                        display: 'inline-block',
                                        position: 'relative',
                                        marginLeft: isSelected ? '40px' : '0',
                                        marginRight: isSelected ? '40px' : '0',
                                        transition: 'margin 0.2s ease',
                                        maxWidth: '100%'
                                    }}
                                >
                                    <span
                                        onMouseEnter={() => setHoveredSentence(sentKey)}
                                        onMouseLeave={() => setHoveredSentence(null)}
                                        onClick={() => {
                                            setSelectedTextSentence(isSelected ? null : sentKey);
                                            setShowTextTranslation(null);
                                        }}
                                        style={{
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'inline',
                                            background: isSelected ? 'rgba(74, 144, 226, 0.15)' : isHovered ? 'rgba(74, 144, 226, 0.05)' : 'transparent',
                                            padding: '2px 4px',
                                            borderRadius: '4px',
                                            position: 'relative',
                                            opacity: selectedTextSentence && !isSelected ? 0.4 : 1
                                        }}
                                    >
                                        {/* Иконка воспроизведения слева */}
                                        {isSelected && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    playSentence(textForTts(sent), voiceId, audioUrl);
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    left: '-35px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    background: '#4a90e2',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '28px',
                                                    height: '28px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '12px',
                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'}
                                                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(-50%) scale(1)'}
                                            >
                                                ▶
                                            </button>
                                        )}
                                        {renderTextWithLineBreaks(mainText)}
                                        
                                        {/* Иконка глазика справа */}
                                        {isSelected && tooltipText && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowTextTranslation(showTextTranslation === sentKey ? null : sentKey);
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    right: '-35px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    background: '#52c41a',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '28px',
                                                    height: '28px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '14px',
                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'}
                                                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(-50%) scale(1)'}
                                            >
                                                <FiEye />
                                            </button>
                                        )}
                                        
                                        {/* Tooltip — английский перевод */}
                                        {showTextTranslation === sentKey && tooltipText && (
                                            <span style={{
                                                position: 'absolute',
                                                bottom: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                marginBottom: '8px',
                                                padding: '8px 12px',
                                                background: '#2c3e50',
                                                color: 'white',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                lineHeight: '1.5',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                zIndex: 1000,
                                                animation: 'fadeIn 0.2s ease',
                                                fontWeight: '400',
                                                maxWidth: '350px',
                                                minWidth: '150px',
                                                whiteSpace: 'normal',
                                                textAlign: 'center'
                                            }}>
                                                {renderTextWithLineBreaks(tooltipText)}
                                                <span style={{
                                                    content: '',
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    width: 0,
                                                    height: 0,
                                                    borderLeft: '6px solid transparent',
                                                    borderRight: '6px solid transparent',
                                                    borderTop: '6px solid #2c3e50'
                                                }} />
                                            </span>
                                        )}
                                    </span>
                                </span>
                            </React.Fragment>
                        );
                    }
                })}
            </div>
        );
    };

    const handleOpenExercise = async (exercise, textIdx, exIdx) => {
        try {
            const response = await studentAPI.getExercise(exercise.id);
            const ex = response.data.exercise;
            const qs = response.data.questions || [];
            const att = lessonExerciseAttempts?.[exercise.id];
            setOpenExercise({
                exercise: ex,
                questions: qs,
                textIdx,
                exIdx,
                lessonId: Number(effectiveLessonId)
            });
            setExerciseAnswers(att?.answers ? { ...att.answers } : {});
            if (ex.exercise_type === 'text_answer') {
                const st = att?.status || 'saved';
                if (st === 'submitted' || st === 'checked' || st === 'needs_revision') {
                    setExerciseResults({
                        manualReview: true,
                        status: st,
                        message: st === 'checked'
                            ? 'Преподаватель проверил работу.'
                            : st === 'needs_revision'
                                ? 'Требуется доработка. Исправьте ответ и отправьте снова на проверку.'
                                : 'Ответ отправлен преподавателю на проверку.',
                        teacherComment: att?.teacher_comment || '',
                        teacherScore: att?.score != null ? att.score : undefined
                    });
                } else {
                    setExerciseResults(null);
                }
            } else if (att && att.total != null && ex.exercise_type !== 'text_answer') {
                setExerciseResults({
                    correct: att.score ?? 0,
                    incorrect: (att.total || 0) - (att.score ?? 0),
                    total: att.total || 0,
                    percentage: att.percentage ?? 0,
                    details: {}
                });
            } else {
                setExerciseResults(null);
            }
        } catch (error) {
            console.error('Ошибка загрузки упражнения:', error);
            alert('Не удалось загрузить упражнение');
        }
    };

    const handleCloseExercise = () => {
        setOpenExercise(null);
        setExerciseAnswers({});
        setExerciseResults(null);
    };

    const handleAnswerChange = (questionId, answer) => {
        setExerciseAnswers(prev => ({
            ...prev,
            [questionId]: answer
        }));
    };

    const handleModalSubmitTextReview = async () => {
        if (!openExercise?.lessonId) return;
        const unanswered = openExercise.questions.filter((q) => {
            const a = exerciseAnswers[q.id];
            return a == null || String(a).trim() === '';
        });
        if (unanswered.length > 0) {
            alert(`Ответьте на все вопросы (осталось ${unanswered.length})`);
            return;
        }
        try {
            await studentAPI.submitExercise(openExercise.exercise.id, {
                lesson_id: openExercise.lessonId,
                answers: exerciseAnswers,
                score: 0,
                total: openExercise.questions.length,
                percentage: null
            });
            setExerciseResults({
                manualReview: true,
                status: 'submitted',
                message: 'Работа отправлена преподавателю на проверку.'
            });
            await refreshExerciseAttempts();
        } catch (e) {
            console.error(e);
            const msg = e.response?.data?.error || 'Не удалось отправить на проверку';
            alert(msg);
        }
    };

    const handleSubmitExercise = async () => {
        const { questions, exercise } = openExercise;
        
        if (!questions || questions.length === 0) {
            alert('Упражнение не содержит вопросов');
            return;
        }

        if (exercise.exercise_type === 'text_answer') {
            return;
        }

        const isPhonetic = exercise?.exercise_type === 'phonetic_dictation';

        // Проверяем, что все вопросы отвечены
        const unanswered = questions.filter((q) => {
            const a = exerciseAnswers[q.id];
            if (isPhonetic) {
                return a === undefined || a === null || String(a).trim() === '';
            }
            return a === undefined || a === null || a === '';
        });
        if (unanswered.length > 0) {
            alert(`Пожалуйста, ответьте на все вопросы. Осталось: ${unanswered.length}`);
            return;
        }

        if (isPhonetic) {
            try {
                const response = await studentAPI.checkExercise({
                    exerciseId: exercise.id,
                    answers: exerciseAnswers
                });
                const { score, total, percentage, results: apiResults } = response.data;
                const details = {};
                apiResults.forEach((r) => {
                    const q = questions.find((x) => x.id === r.questionId);
                    details[r.questionId] = {
                        correct: r.isCorrect,
                        userAnswer: exerciseAnswers[r.questionId],
                        correctAnswer: r.correctAnswer,
                        questionText: q?.question_text || ''
                    };
                });
                setExerciseResults({
                    correct: score,
                    incorrect: total - score,
                    total,
                    percentage,
                    details
                });
                if (openExercise.lessonId) {
                    try {
                        await studentAPI.saveExerciseAttempt(openExercise.exercise.id, {
                            lesson_id: openExercise.lessonId,
                            answers: exerciseAnswers,
                            score,
                            total,
                            percentage
                        });
                        await refreshExerciseAttempts();
                    } catch (e) {
                        console.error(e);
                    }
                }
            } catch (err) {
                console.error(err);
                alert('Не удалось проверить ответы');
            }
            return;
        }

        // Проверяем ответы (клиентская проверка для остальных типов)
        const results = {
            correct: 0,
            incorrect: 0,
            total: questions.length,
            details: {}
        };

        questions.forEach(question => {
            const userAnswer = exerciseAnswers[question.id];
            
            let isCorrect = false;
            let correctAnswerText = question.correct_answer || 'Не указан';
            let userAnswerText = String(userAnswer || 'Не указан');
            
            // Проверка в зависимости от типа вопроса
            if (openExercise.exercise.exercise_type === 'multiple_choice') {
                // Для multiple choice нужно получить опции и сравнить
                try {
                    const options = typeof question.question_data === 'string' 
                        ? JSON.parse(question.question_data) 
                        : question.question_data;
                    
                    if (Array.isArray(options) && options.length > 0) {
                        const userAnswerIndex = parseInt(userAnswer);
                        userAnswerText = options[userAnswerIndex] || String(userAnswer);
                        
                        // Проверяем, correct_answer - это индекс или текст
                        const correctAnswerAsNumber = parseInt(question.correct_answer);
                        
                        if (!isNaN(correctAnswerAsNumber) && correctAnswerAsNumber >= 0 && correctAnswerAsNumber < options.length) {
                            // correct_answer - это валидный индекс
                            correctAnswerText = options[correctAnswerAsNumber];
                            isCorrect = userAnswerIndex === correctAnswerAsNumber;
                        } else if (question.correct_answer && typeof question.correct_answer === 'string') {
                            // correct_answer - это текст, ищем его в options
                            correctAnswerText = question.correct_answer;
                            const correctIndex = options.findIndex(opt => 
                                String(opt).trim().toLowerCase() === String(question.correct_answer).trim().toLowerCase()
                            );
                            
                            if (correctIndex !== -1) {
                                isCorrect = userAnswerIndex === correctIndex;
                            } else {
                                // Если не нашли в options, сравниваем текст напрямую
                                isCorrect = String(userAnswerText).trim().toLowerCase() === String(correctAnswerText).trim().toLowerCase();
                            }
                        }
                    }
                } catch (e) {
                    console.error('Ошибка парсинга опций:', e, 'question:', question);
                    // Fallback - сравниваем как есть
                    correctAnswerText = question.correct_answer || 'Не указан';
                    isCorrect = String(userAnswer) === String(question.correct_answer);
                }
            } else if (openExercise.exercise.exercise_type === 'true_false') {
                // Для true/false/not_stated
                const norm = (v) => {
                    const s = String(v ?? '').trim().toLowerCase();
                    if (s === 'true') return 'true';
                    if (s === 'false') return 'false';
                    if (s === 'not_stated' || s === 'not stated' || s === 'not-stated') return 'not_stated';
                    return s;
                };
                const pretty = (v) => v === 'true'
                    ? '✓ Правда (True)'
                    : v === 'false'
                        ? '✗ Ложь (False)'
                        : v === 'not_stated'
                            ? '~ Не указано (Not stated)'
                            : 'Не указан';
                const c = norm(question.correct_answer);
                const u = norm(userAnswer);
                correctAnswerText = pretty(c);
                userAnswerText = pretty(u);
                isCorrect = u === c;
            } else if (openExercise.exercise.exercise_type === 'fill_gaps') {
                // Пропуски: сравниваем строки (нечувствительно к регистру)
                correctAnswerText = question.correct_answer || 'Не указан';
                userAnswerText = String(userAnswer || 'Не указан');
                isCorrect = String(userAnswer || '').trim().toLowerCase() === 
                           String(question.correct_answer || '').trim().toLowerCase();
            } else {
                // Для остальных типов - точное совпадение
                correctAnswerText = question.correct_answer || 'Не указан';
                userAnswerText = String(userAnswer || 'Не указан');
                isCorrect = String(userAnswer) === String(question.correct_answer);
            }
            
            if (isCorrect) {
                results.correct++;
            } else {
                results.incorrect++;
            }
            
            results.details[question.id] = {
                correct: isCorrect,
                userAnswer: userAnswerText,
                correctAnswer: correctAnswerText,
                questionText: question.question_text
            };
        });

        // Вычисляем процент
        results.percentage = Math.round((results.correct / results.total) * 100);

        setExerciseResults(results);
        if (openExercise.lessonId) {
            try {
                await studentAPI.saveExerciseAttempt(openExercise.exercise.id, {
                    lesson_id: openExercise.lessonId,
                    answers: exerciseAnswers,
                    score: results.correct,
                    total: results.total,
                    percentage: results.percentage
                });
                await refreshExerciseAttempts();
            } catch (e) {
                console.error(e);
            }
        }
    };

    const renderAudioPlayer = (text, textIdx) => {
        const playerKey = `player-${textIdx}`;
        const playerState = audioPlayers[playerKey] || {};
        const isPlaying = !!playerState.isPlaying;
        const isPaused = !!playerState.isPaused;
        const isLoading = !!playerState.isLoading;
        const progress = playerState.progress ?? 0; // 0..1
        const sentences = text.full_text_en ? parseSentencesFromFullText(text.full_text_en) : [];

        const updatePlayer = (updates) => {
            setAudioPlayers(prev => ({
                ...prev,
                [playerKey]: { ...prev[playerKey], ...updates }
            }));
        };

        const playAll = () => {
            const currentPlayer = audioPlayers[playerKey] || {};
            const { audioInstance } = currentPlayer;

            // Уже что‑то играет: пауза / продолжить
            if (isPlaying) {
                if (text.audio_url && audioInstance) {
                    // Для загруженного аудио
                    if (isPaused) {
                        audioInstance.play();
                        updatePlayer({ isPaused: false });
                    } else {
                        audioInstance.pause();
                        updatePlayer({ isPaused: true });
                    }
                } else {
                    // Для TTS
                    if (isPaused) {
                        ttsService.resume();
                        updatePlayer({ isPaused: false });
                    } else {
                        ttsService.pause();
                        updatePlayer({ isPaused: true });
                    }
                }
                return;
            }

            // На всякий случай стоп предыдущего аудио
            ttsService.stop();
            if (audioInstance) {
                audioInstance.pause();
                audioInstance.currentTime = 0;
            }

            // Показываем загрузку
            updatePlayer({ isLoading: true, isPlaying: false, isPaused: false, progress: 0, audioInstance: null, currentTime: 0, duration: 0 });

            // Если есть загруженное аудио для текста, используем его
            if (text.audio_url) {
                const audio = new Audio(`${API_ORIGIN}${text.audio_url}`);
                audio.playbackRate = playbackSpeed;
                
                audio.onloadeddata = () => {
                    updatePlayer({ isLoading: false, isPlaying: true, isPaused: false, progress: 0, audioInstance: audio, duration: audio.duration });
                };
                
                audio.ontimeupdate = () => {
                    const p = audio.duration ? audio.currentTime / audio.duration : 0;
                    updatePlayer({ progress: p, currentTime: audio.currentTime });
                };
                
                audio.onended = () => {
                    updatePlayer({ isLoading: false, isPlaying: false, isPaused: false, progress: 1, audioInstance: null });
                };
                
                audio.onerror = (error) => {
                    console.error('Ошибка воспроизведения загруженного аудио:', error);
                    updatePlayer({ isLoading: false, isPlaying: false, isPaused: false, audioInstance: null });
                };
                
                audio.play().catch(error => {
                    console.error('Ошибка запуска аудио:', error);
                    updatePlayer({ isLoading: false, isPlaying: false, isPaused: false, audioInstance: null });
                });
            } else {
                // Иначе используем TTS
                if (!sentences.length) {
                    updatePlayer({ isLoading: false });
                    return;
                }

                // Убираем разделители | из текста
                const fullText = sentences.map(s => s.trim().replace(/\s*\|\s*/g, ' ')).join(' ');
                if (!fullText) {
                    updatePlayer({ isLoading: false });
                    return;
                }

                ttsService.speak(fullText, {
                    rate: playbackSpeed,
                    lang: 'en-US',
                    voice: text.voice_id || 'en-US-JennyNeural',
                    onStart: () => {
                        updatePlayer({ isLoading: false, isPlaying: true, isPaused: false, progress: 0 });
                    },
                    onProgress: (p) => {
                        updatePlayer({ progress: p });
                    },
                    onEnd: () => {
                        updatePlayer({ isLoading: false, isPlaying: false, isPaused: false, progress: 1 });
                    }
                }).catch(error => {
                    console.error('Ошибка воспроизведения полного текста:', error);
                    updatePlayer({ isLoading: false, isPlaying: false, isPaused: false });
                });
            }
        };
        
        const handleProgressClick = (e) => {
            const currentPlayer = audioPlayers[playerKey] || {};
            const { audioInstance } = currentPlayer;
            
            if (!text.audio_url || !audioInstance) return;
            
            const rect = e.currentTarget.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const percentage = Math.max(0, Math.min(1, clickX / rect.width));
            const newTime = audioInstance.duration * percentage;
            
            audioInstance.currentTime = newTime;
            updatePlayer({ progress: percentage, currentTime: newTime });
        };
        
        const formatTime = (seconds) => {
            if (!seconds || isNaN(seconds)) return '0:00';
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        };

        return (
            <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '20px',
                borderRadius: '12px',
                marginTop: '25px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <button
                        onClick={playAll}
                        disabled={isLoading}
                        style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            border: 'none',
                            background: 'white',
                            color: '#667eea',
                            fontSize: '20px',
                            cursor: isLoading ? 'wait' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            transition: 'all 0.3s',
                            opacity: isLoading ? 0.7 : 1
                        }}
                        onMouseEnter={(e) => !isLoading && (e.currentTarget.style.transform = 'scale(1.1)')}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                        {isLoading ? (
                            <div style={{
                                width: '24px',
                                height: '24px',
                                border: '3px solid #667eea',
                                borderTopColor: 'transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }} />
                        ) : (
                            isPlaying && !isPaused ? '⏸' : '▶'
                        )}
                    </button>

                    <div style={{ flex: 1 }}>
                        <div style={{
                            color: 'white',
                            fontSize: '14px',
                            marginBottom: '8px',
                            fontWeight: '500'
                        }}>
                            {isLoading ? 'Загрузка аудио...' : 'Прослушать весь текст'}
                        </div>
                        
                        <div 
                            onClick={handleProgressClick}
                            style={{
                                height: '6px',
                                background: 'rgba(255,255,255,0.3)',
                                borderRadius: '3px',
                                overflow: 'hidden',
                                cursor: text.audio_url ? 'pointer' : 'default',
                                position: 'relative'
                            }}
                        >
                            <div style={{
                                height: '100%',
                                background: 'white',
                                borderRadius: '3px',
                                width: `${Math.max(0, Math.min(100, progress * 100))}%`,
                                transition: 'width 0.2s linear',
                                boxShadow: progress > 0 ? '0 0 8px rgba(255,255,255,0.5)' : 'none'
                            }} />
                        </div>
                        {text.audio_url && playerState.duration > 0 && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                color: 'rgba(255,255,255,0.8)',
                                fontSize: '11px',
                                marginTop: '4px'
                            }}>
                                <span>{formatTime(playerState.currentTime || 0)}</span>
                                <span>{formatTime(playerState.duration)}</span>
                            </div>
                        )}
                    </div>

                    <div style={{
                        color: 'white',
                        fontSize: '12px',
                        background: 'rgba(255,255,255,0.2)',
                        padding: '4px 10px',
                        borderRadius: '20px'
                    }}>
                        {playbackSpeed}x
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return <div className="loading">Загрузка урока...</div>;
    }

    if (!lesson) {
        return (
            <div className="container">
                <div className="card">
                    <p style={{ textAlign: 'center' }}>Урок не найден</p>
                    <button onClick={() => navigate('/student/lessons')} className="btn">
                        Вернуться к урокам
                    </button>
                </div>
            </div>
        );
    }

    const hasSentences = lesson.standaloneSentences && lesson.standaloneSentences.length > 0;
    const hasTexts = lesson.texts && lesson.texts.length > 0;
    const hasVideos = lesson.videos && lesson.videos.length > 0;

    // Модальное окно с упражнением
    const renderExerciseModal = () => {
        if (!openExercise) return null;

        const { exercise, questions } = openExercise;
        const modalFieldDisabled = exercise.exercise_type === 'text_answer'
            ? !!(exerciseResults?.manualReview && exerciseResults?.status !== 'needs_revision')
            : !!exerciseResults;
        const showModalTextSubmit = exercise.exercise_type === 'text_answer'
            && (!exerciseResults || (exerciseResults.manualReview && exerciseResults.status === 'needs_revision'));

        return (
            <div 
                className="modal-overlay"
                onClick={handleCloseExercise}
            >
                <div 
                    className="modal-content"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="modal-header">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 style={{ margin: 0, fontSize: '22px' }}>
                                {exercise.title}
                            </h2>
                            <button
                                onClick={handleCloseExercise}
                                className="modal-close-btn"
                            >
                                ×
                            </button>
                        </div>
                        {exercise.instructions && (
                            <p style={{ margin: '10px 0 0 0', color: '#666', fontSize: '14px' }}>
                                {exercise.instructions}
                            </p>
                        )}
                    </div>

                    {/* Content */}
                    <div className="modal-body">
                        {questions && questions.length > 0 ? (
                            <div>
                                {exercise.exercise_type !== 'phonetic_dictation' && (
                                    <QuestionPromptAudio
                                        questionData={typeof questions[0].question_data === 'string' ? JSON.parse(questions[0].question_data) : (questions[0].question_data || {})}
                                        disabled={modalFieldDisabled}
                                    />
                                )}
                                {questions.map((question, qIdx) => {
                                    const questionData = typeof question.question_data === 'string' 
                                        ? JSON.parse(question.question_data) 
                                        : question.question_data || [];

                                    return (
                                        <div key={qIdx} style={{
                                            marginBottom: '30px',
                                            padding: '20px',
                                            background: '#f8f9fa',
                                            borderRadius: '8px',
                                            border: '1px solid #e8e8e8'
                                        }}>
                                            <div style={{ marginBottom: '15px' }}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    background: '#667eea',
                                                    color: 'white',
                                                    padding: '4px 12px',
                                                    borderRadius: '20px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    marginBottom: '10px'
                                                }}>
                                                    Вопрос {qIdx + 1}
                                                </span>
                                                <p style={{ 
                                                    fontSize: '16px',
                                                    fontWeight: '500',
                                                    margin: '8px 0 0 0',
                                                    color: '#2c3e50'
                                                }}>
                                                    {question.question_text}
                                                </p>
                                            </div>

                                            {/* Multiple Choice */}
                                            {exercise.exercise_type === 'multiple_choice' && questionData.options && questionData.options.length > 0 && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                    {questionData.options.map((option, optIdx) => (
                                                        <label
                                                            key={optIdx}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                padding: '12px 16px',
                                                                background: exerciseAnswers[question.id] === optIdx ? '#e3f2fd' : 'white',
                                                                border: exerciseAnswers[question.id] === optIdx ? '2px solid #4a90e2' : '2px solid #e0e0e0',
                                                                borderRadius: '8px',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if (exerciseAnswers[question.id] !== optIdx) {
                                                                    e.currentTarget.style.borderColor = '#bbb';
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if (exerciseAnswers[question.id] !== optIdx) {
                                                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                                                }
                                                            }}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name={`question-${question.id}`}
                                                                checked={exerciseAnswers[question.id] === optIdx}
                                                                onChange={() => handleAnswerChange(question.id, optIdx)}
                                                                style={{ marginRight: '12px', cursor: 'pointer' }}
                                                            />
                                                            <span style={{ fontSize: '15px' }}>{option}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )}

                                            {/* True/False */}
                                            {exercise.exercise_type === 'true_false' && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                    <label
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '12px 16px',
                                                            background: exerciseAnswers[question.id] === 'true' ? '#e8f5e9' : 'white',
                                                            border: exerciseAnswers[question.id] === 'true' ? '2px solid #52c41a' : '2px solid #e0e0e0',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (exerciseAnswers[question.id] !== 'true') {
                                                                e.currentTarget.style.borderColor = '#bbb';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (exerciseAnswers[question.id] !== 'true') {
                                                                e.currentTarget.style.borderColor = '#e0e0e0';
                                                            }
                                                        }}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={`question-${question.id}`}
                                                            checked={exerciseAnswers[question.id] === 'true'}
                                                            onChange={() => handleAnswerChange(question.id, 'true')}
                                                            style={{ marginRight: '12px', cursor: 'pointer' }}
                                                        />
                                                        <span style={{ fontSize: '15px', fontWeight: '500', color: '#52c41a' }}>✓ Правда (True)</span>
                                                    </label>
                                                    <label
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '12px 16px',
                                                            background: exerciseAnswers[question.id] === 'false' ? '#ffebee' : 'white',
                                                            border: exerciseAnswers[question.id] === 'false' ? '2px solid #ff4d4f' : '2px solid #e0e0e0',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (exerciseAnswers[question.id] !== 'false') {
                                                                e.currentTarget.style.borderColor = '#bbb';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (exerciseAnswers[question.id] !== 'false') {
                                                                e.currentTarget.style.borderColor = '#e0e0e0';
                                                            }
                                                        }}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={`question-${question.id}`}
                                                            checked={exerciseAnswers[question.id] === 'false'}
                                                            onChange={() => handleAnswerChange(question.id, 'false')}
                                                            style={{ marginRight: '12px', cursor: 'pointer' }}
                                                        />
                                                        <span style={{ fontSize: '15px', fontWeight: '500', color: '#ff4d4f' }}>✗ Ложь (False)</span>
                                                    </label>
                                                    <label
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '12px 16px',
                                                            background: exerciseAnswers[question.id] === 'not_stated' ? '#fff7e6' : 'white',
                                                            border: exerciseAnswers[question.id] === 'not_stated' ? '2px solid #fa8c16' : '2px solid #e0e0e0',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={`question-${question.id}`}
                                                            checked={exerciseAnswers[question.id] === 'not_stated'}
                                                            onChange={() => handleAnswerChange(question.id, 'not_stated')}
                                                            style={{ marginRight: '12px', cursor: 'pointer' }}
                                                        />
                                                        <span style={{ fontSize: '15px', fontWeight: '500', color: '#fa8c16' }}>~ Не указано (Not stated)</span>
                                                    </label>
                                                </div>
                                            )}

                                            {/* Fill in the gaps */}
                                            {exercise.exercise_type === 'fill_gaps' && questionData.sentence && (
                                                <div>
                                                    <p style={{ 
                                                        fontSize: '15px', 
                                                        marginBottom: '15px',
                                                        lineHeight: '1.6',
                                                        color: '#2c3e50'
                                                    }}>
                                                        {questionData.sentence}
                                                    </p>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                        {questionData.sentence.split('___').length - 1 > 0 ? (
                                                            Array.from({ length: questionData.sentence.split('___').length - 1 }).map((_, gapIdx) => (
                                                                <div key={gapIdx}>
                                                                    <label style={{ 
                                                                        fontSize: '13px', 
                                                                        color: '#666', 
                                                                        marginBottom: '5px',
                                                                        display: 'block'
                                                                    }}>
                                                                        Пропуск {gapIdx + 1}:
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        value={(exerciseAnswers[question.id] || [])[gapIdx] || ''}
                                                                        onChange={(e) => {
                                                                            const currentAnswers = exerciseAnswers[question.id] || [];
                                                                            const newAnswers = [...currentAnswers];
                                                                            newAnswers[gapIdx] = e.target.value;
                                                                            handleAnswerChange(question.id, newAnswers);
                                                                        }}
                                                                        placeholder={`Введите слово для пропуска ${gapIdx + 1}`}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '12px 16px',
                                                                            border: '2px solid #e0e0e0',
                                                                            borderRadius: '8px',
                                                                            fontSize: '15px',
                                                                            outline: 'none',
                                                                            transition: 'border-color 0.2s'
                                                                        }}
                                                                        onFocus={(e) => e.target.style.borderColor = '#4a90e2'}
                                                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                                                    />
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <p style={{ color: '#999', fontStyle: 'italic' }}>
                                                                В предложении нет пропусков
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Match - сопоставление пар */}
                                            {exercise.exercise_type === 'match' && questionData.pairs && questionData.pairs.length > 0 && (
                                                <div>
                                                    {/* Отображаем все пары для сопоставления */}
                                                    {questionData.pairs.map((pair, pairIdx) => (
                                                        <div key={pairIdx} style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: '1fr auto 1fr',
                                                            gap: '15px',
                                                            alignItems: 'center',
                                                            marginBottom: '15px'
                                                        }}>
                                                            <div style={{
                                                                padding: '12px 16px',
                                                                background: '#f0f7ff',
                                                                border: '2px solid #d0e7ff',
                                                                borderRadius: '8px',
                                                                fontSize: '15px',
                                                                fontWeight: '500',
                                                                color: '#2c3e50',
                                                                textAlign: 'center'
                                                            }}>
                                                                {pair.left}
                                                            </div>
                                                            <div style={{ fontSize: '20px', color: '#999' }}>⟷</div>
                                                            <select
                                                                value={(exerciseAnswers[question.id] || {})[pairIdx] || ''}
                                                                onChange={(e) => {
                                                                    const currentAnswers = exerciseAnswers[question.id] || {};
                                                                    handleAnswerChange(question.id, {
                                                                        ...currentAnswers,
                                                                        [pairIdx]: e.target.value
                                                                    });
                                                                }}
                                                                style={{
                                                                    padding: '12px 16px',
                                                                    border: '2px solid #e0e0e0',
                                                                    borderRadius: '8px',
                                                                    fontSize: '15px',
                                                                    cursor: 'pointer',
                                                                    background: (exerciseAnswers[question.id] || {})[pairIdx] ? '#e3f2fd' : '#fafafa'
                                                                }}
                                                            >
                                                                <option value="">Выберите пару...</option>
                                                                {questionData.pairs.map((p, optIdx) => (
                                                                    <option key={optIdx} value={p.right}>
                                                                        {p.right}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    ))}
                                                    <small style={{ display: 'block', marginTop: '8px', color: '#999', fontStyle: 'italic' }}>
                                                        Выберите соответствие для каждой пары
                                                    </small>
                                                </div>
                                            )}

                                            {/* Arrange - расставить слова */}
                                            {exercise.exercise_type === 'arrange' && questionData.words && questionData.words.length > 0 && (
                                                <div>
                                                    <p style={{ 
                                                        fontSize: '14px', 
                                                        color: '#666', 
                                                        marginBottom: '15px',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Перетащите слова или нажмите на них, чтобы составить правильное предложение
                                                    </p>
                                                    
                                                    {/* Зона для составления предложения */}
                                                    <div style={{
                                                        minHeight: '60px',
                                                        padding: '15px',
                                                        background: '#f8f9fa',
                                                        border: '2px dashed #d0e7ff',
                                                        borderRadius: '8px',
                                                        marginBottom: '15px',
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: '8px',
                                                        alignItems: 'center'
                                                    }}>
                                                        {(exerciseAnswers[question.id] || []).length > 0 ? (
                                                            (exerciseAnswers[question.id] || []).map((word, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    onClick={() => {
                                                                        const currentAnswer = [...(exerciseAnswers[question.id] || [])];
                                                                        currentAnswer.splice(idx, 1);
                                                                        handleAnswerChange(question.id, currentAnswer);
                                                                    }}
                                                                    style={{
                                                                        padding: '8px 16px',
                                                                        background: '#4a90e2',
                                                                        color: 'white',
                                                                        borderRadius: '20px',
                                                                        fontSize: '15px',
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s',
                                                                        userSelect: 'none'
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.background = '#357abd'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.background = '#4a90e2'}
                                                                >
                                                                    {word}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <span style={{ color: '#999', fontSize: '14px' }}>
                                                                Нажмите на слова ниже, чтобы составить предложение
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Доступные слова (перемешанные) */}
                                                    <div style={{
                                                        padding: '15px',
                                                        background: 'white',
                                                        border: '2px solid #e0e0e0',
                                                        borderRadius: '8px',
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: '8px'
                                                    }}>
                                                        {(() => {
                                                            const usedWords = exerciseAnswers[question.id] || [];
                                                            const shuffledWords = [...questionData.words]
                                                                .sort(() => Math.random() - 0.5);
                                                            
                                                            return shuffledWords.map((word, idx) => {
                                                                const wordCount = questionData.words.filter(w => w === word).length;
                                                                const usedCount = usedWords.filter(w => w === word).length;
                                                                const isAvailable = usedCount < wordCount;
                                                                
                                                                if (!isAvailable) return null;
                                                                
                                                                return (
                                                                    <div
                                                                        key={`${word}-${idx}`}
                                                                        onClick={() => {
                                                                            const currentAnswer = [...(exerciseAnswers[question.id] || [])];
                                                                            currentAnswer.push(word);
                                                                            handleAnswerChange(question.id, currentAnswer);
                                                                        }}
                                                                        style={{
                                                                            padding: '8px 16px',
                                                                            background: '#f0f7ff',
                                                                            color: '#2c3e50',
                                                                            border: '2px solid #d0e7ff',
                                                                            borderRadius: '20px',
                                                                            fontSize: '15px',
                                                                            cursor: 'pointer',
                                                                            transition: 'all 0.2s',
                                                                            userSelect: 'none'
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.background = '#e3f2fd';
                                                                            e.currentTarget.style.borderColor = '#4a90e2';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.background = '#f0f7ff';
                                                                            e.currentTarget.style.borderColor = '#d0e7ff';
                                                                        }}
                                                                    >
                                                                        {word}
                                                                    </div>
                                                                );
                                                            }).filter(Boolean);
                                                        })()}
                                                    </div>
                                                    
                                                    {/* Кнопка сброса */}
                                                    {(exerciseAnswers[question.id] || []).length > 0 && (
                                                        <button
                                                            onClick={() => handleAnswerChange(question.id, [])}
                                                            style={{
                                                                marginTop: '10px',
                                                                padding: '8px 16px',
                                                                background: 'transparent',
                                                                color: '#ff4d4f',
                                                                border: '1px solid #ff4d4f',
                                                                borderRadius: '6px',
                                                                fontSize: '13px',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            Сбросить
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            {/* Text answer */}
                                            {exercise.exercise_type === 'text_answer' && (
                                                <div>
                                                    <textarea
                                                        value={exerciseAnswers[question.id] || ''}
                                                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                                        disabled={modalFieldDisabled}
                                                        placeholder="Напишите развёрнутый ответ…"
                                                        rows={5}
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px 16px',
                                                            border: '2px solid #e0e0e0',
                                                            borderRadius: '8px',
                                                            fontSize: '15px',
                                                            outline: 'none',
                                                            resize: 'vertical',
                                                            fontFamily: 'inherit',
                                                            transition: 'border-color 0.2s'
                                                        }}
                                                        onFocus={(e) => { if (!modalFieldDisabled) e.target.style.borderColor = '#4a90e2'; }}
                                                        onBlur={(e) => { e.target.style.borderColor = '#e0e0e0'; }}
                                                    />
                                                    <p style={{ fontSize: '12px', color: '#888', margin: '8px 0 0 0' }}>
                                                        Проверяет преподаватель. Нажмите «Отправить на проверку», когда будете готовы.
                                                    </p>
                                                </div>
                                            )}

                                            {exercise.exercise_type === 'phonetic_dictation' && (
                                                <div>
                                                    <PhoneticDictationListenButton question={question} disabled={modalFieldDisabled} />
                                                    <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '6px' }}>
                                                        Напишите услышанное по-английски:
                                                    </label>
                                                    <textarea
                                                        value={exerciseAnswers[question.id] || ''}
                                                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                                        disabled={modalFieldDisabled}
                                                        rows={4}
                                                        placeholder="Введите предложение…"
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px 16px',
                                                            border: '2px solid #e0e0e0',
                                                            borderRadius: '8px',
                                                            fontSize: '15px',
                                                            outline: 'none',
                                                            resize: 'vertical',
                                                            fontFamily: 'inherit'
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p style={{ textAlign: 'center', color: '#999', padding: '40px 20px' }}>
                                К этому упражнению пока не добавлены вопросы
                            </p>
                        )}
                    </div>

                    {/* Results */}
                    {exerciseResults && (
                        <div style={{
                            padding: '20px',
                            borderTop: '2px solid #e8e8e8',
                            background: exerciseResults.manualReview ? '#e6f7ff' : ((exerciseResults.percentage || 0) >= 70 ? '#f6ffed' : '#fff2f0')
                        }}>
                            {exerciseResults.manualReview ? (
                                <div style={{ fontSize: '15px', color: '#2c3e50' }}>
                                    <p style={{ margin: '0 0 10px 0', textAlign: 'center' }}>{exerciseResults.message}</p>
                                    {exerciseResults.status === 'checked' && exerciseResults.teacherScore != null && (
                                        <p style={{ margin: '0 0 10px 0', textAlign: 'center', fontWeight: 600 }}>
                                            Баллы: {exerciseResults.teacherScore} из 100
                                        </p>
                                    )}
                                    {exerciseResults.teacherComment ? (
                                        <div style={{
                                            marginTop: '8px',
                                            padding: '12px',
                                            background: 'white',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}>
                                            <strong>Комментарий преподавателя:</strong>
                                            <p style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>{exerciseResults.teacherComment}</p>
                                        </div>
                                    ) : null}
                                </div>
                            ) : (
                                <>
                            <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                                <div style={{
                                    width: '70px',
                                    height: '70px',
                                    borderRadius: '50%',
                                    background: (exerciseResults.percentage || 0) >= 70 ? '#52c41a' : '#ff4d4f',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 10px',
                                    fontSize: '24px',
                                    fontWeight: 'bold'
                                }}>
                                    {exerciseResults.percentage || 0}%
                                </div>
                                <h3 style={{ 
                                    fontSize: '16px',
                                    color: (exerciseResults.percentage || 0) >= 70 ? '#52c41a' : '#ff4d4f',
                                    margin: '0 0 6px 0'
                                }}>
                                    {(exerciseResults.percentage || 0) >= 70 ? 'Отлично!' : 'Нужно еще поработать'}
                                </h3>
                                <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>
                                    Правильных ответов: {exerciseResults.correct} из {exerciseResults.total}
                                </p>
                            </div>

                            <div style={{ marginTop: '15px' }}>
                                <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#333' }}>
                                    Ваши ответы:
                                </h4>
                                {Object.entries(exerciseResults.details || {}).map(([questionId, detail], idx) => (
                                    <div
                                        key={questionId}
                                        style={{
                                            padding: '10px 12px',
                                            background: detail.correct ? '#f6ffed' : '#fff2f0',
                                            border: `1px solid ${detail.correct ? '#b7eb8f' : '#ffccc7'}`,
                                            borderRadius: '6px',
                                            marginBottom: '8px'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                            <div style={{
                                                minWidth: '22px',
                                                height: '22px',
                                                borderRadius: '50%',
                                                background: detail.correct ? '#52c41a' : '#ff4d4f',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                flexShrink: 0
                                            }}>
                                                {detail.correct ? '✓' : '✗'}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <p style={{ 
                                                    margin: '0 0 5px 0', 
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    color: '#333'
                                                }}>
                                                    {detail.questionText?.trim() ? detail.questionText : 'Ответ'}
                                                </p>
                                                <div style={{ fontSize: '12px' }}>
                                                    <div style={{ marginBottom: '3px' }}>
                                                        <strong>Ваш ответ:</strong> <span style={{ color: detail.correct ? '#52c41a' : '#ff4d4f' }}>
                                                            {detail.userAnswer !== undefined && detail.userAnswer !== null ? String(detail.userAnswer) : 'Не указан'}
                                                        </span>
                                                    </div>
                                                    {!detail.correct && (
                                                        <div>
                                                            <strong>Правильный ответ:</strong> <span style={{ color: '#52c41a' }}>
                                                                {detail.correctAnswer !== undefined && detail.correctAnswer !== null ? String(detail.correctAnswer) : 'Не указан'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div style={{ textAlign: 'center', marginTop: '15px' }}>
                                <button
                                    onClick={() => {
                                        setExerciseResults(null);
                                        setExerciseAnswers({});
                                    }}
                                    style={{
                                        padding: '8px 24px',
                                        background: '#667eea',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Попробовать снова
                                </button>
                            </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Footer */}
                    <div style={{
                        padding: '20px 30px',
                        borderTop: '1px solid #e8e8e8',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                        gap: '10px'
                    }}>
                        <button
                            onClick={handleCloseExercise}
                            style={{
                                padding: '10px 24px',
                                border: '1px solid #ddd',
                                background: 'white',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            Закрыть
                        </button>
                        {!exerciseResults && exercise.exercise_type !== 'text_answer' && (
                            <button
                                onClick={handleSubmitExercise}
                                style={{
                                    padding: '10px 24px',
                                    border: 'none',
                                    background: '#667eea',
                                    color: 'white',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}
                            >
                                Проверить
                            </button>
                        )}
                        {showModalTextSubmit && (
                            <button
                                type="button"
                                onClick={handleModalSubmitTextReview}
                                style={{
                                    padding: '10px 24px',
                                    border: 'none',
                                    background: '#667eea',
                                    color: 'white',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}
                            >
                                {exerciseResults?.status === 'needs_revision'
                                    ? 'Отправить исправленный ответ'
                                    : 'Отправить на проверку'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {/* Оверлей для мобильного сайдбара */}
            {mobileSidebarOpen && (
                <div 
                    className="mobile-overlay active" 
                    onClick={() => setMobileSidebarOpen(false)}
                />
            )}

            {/* Единое боковое меню */}
            <aside className={`lesson-sidebar ${mobileSidebarOpen ? 'mobile-sidebar-open' : ''}`}>
                {!backTo && (
                <div className="lesson-sidebar-header">
                    <div className="lesson-sidebar-tabs">
                        <button 
                            className={`lesson-sidebar-tab ${sidebarCategory === 'grammar' ? 'active' : ''}`}
                            onClick={async () => {
                                setSidebarCategory('grammar');
                                const areasRes = await studentAPI.getAreas({ category: 'grammar' });
                                setSidebarAreas(areasRes.data.areas || []);
                                setSidebarSelectedArea(null);
                                setSidebarLessons([]);
                            }}
                        >
                            <FiBook size={14} />
                            Грамматика
                        </button>
                        <button 
                            className={`lesson-sidebar-tab ${sidebarCategory === 'vocabulary' ? 'active' : ''}`}
                            onClick={async () => {
                                setSidebarCategory('vocabulary');
                                const areasRes = await studentAPI.getAreas({ category: 'vocabulary' });
                                setSidebarAreas(areasRes.data.areas || []);
                                setSidebarSelectedArea(null);
                                setSidebarLessons([]);
                            }}
                        >
                            <FiFileText size={14} />
                            Лексика
                        </button>
                    </div>
                </div>
                )}

                <div className="lesson-sidebar-areas">
                    {!backTo && (
                        <>
                            <div style={{ padding: '8px 0 5px', fontSize: '9px', fontWeight: '600', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Области обучения
                            </div>
                            {sidebarAreas.map(area => (
                                <div
                                    key={area.id}
                                    className={`lesson-sidebar-area ${sidebarSelectedArea === area.id ? 'active' : ''}`}
                                    onClick={async () => {
                                        setSidebarSelectedArea(area.id);
                                        // Загружаем уроки этой области
                                        const levelsRes = await studentAPI.getLevels(area.id);
                                        const levels = levelsRes.data.levels || [];
                                        const lessonsPromises = levels.map(level =>
                                            studentAPI.getLessons(level.id).then(res => res.data.lessons || [])
                                        );
                                        const allLessonsArrays = await Promise.all(lessonsPromises);
                                        const allLessons = allLessonsArrays.flat().filter(l => l.category === sidebarCategory);
                                        setSidebarLessons(allLessons);
                                    }}
                                >
                                    {area.name}
                                </div>
                            ))}
                        </>
                    )}
                    
                    {/* Контролы скорости */}
                    <div style={{ 
                        marginTop: '10px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        borderTop: '1px solid #e8e8e8'
                    }}>
                        <label style={{ fontSize: '9px', color: '#999', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>
                            Скорость воспроизведения
                        </label>
                        <select 
                            value={playbackSpeed} 
                            onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
                            style={{ 
                                width: '100%',
                                padding: '5px',
                                borderRadius: '4px',
                                border: '1px solid #e0e0e0',
                                fontSize: '11px',
                                background: 'white',
                                cursor: 'pointer'
                            }}
                        >
                            <option value="0.5">0.5x (медленно)</option>
                            <option value="0.75">0.75x</option>
                            <option value="1">1x (нормально)</option>
                            <option value="1.25">1.25x</option>
                            <option value="1.5">1.5x (быстро)</option>
                        </select>
                    </div>
                </div>

                <div className="lesson-sidebar-lessons">
                    {backTo && lesson && lesson.hasBlocks && lesson.blocks && (
                        <>
                            <div style={{ padding: '0 0 10px', fontSize: '11px', fontWeight: '600', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Блоки
                            </div>
                            {lesson.blocks.map((block, blockIdx) => (
                                <div key={blockIdx} style={{ marginBottom: '4px' }}>
                                    <div
                                        ref={currentBlockIndex === blockIdx ? activeSidebarBlockRef : null}
                                        onClick={() => setCurrentBlockIndex(blockIdx)}
                                        style={{
                                            padding: '8px 10px',
                                            marginBottom: '4px',
                                            cursor: 'pointer',
                                            borderRadius: '4px',
                                            background: currentBlockIndex === blockIdx ? '#f0f7ff' : 'transparent',
                                            color: currentBlockIndex === blockIdx ? '#1976d2' : '#666',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            transition: 'all 0.2s',
                                            borderLeft: currentBlockIndex === blockIdx ? '2px solid #1976d2' : '2px solid transparent'
                                        }}
                                    >
                                        {block.title}
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                    {!backTo && sidebarSelectedArea && (
                        <>
                            <div style={{ padding: '0 0 10px', fontSize: '11px', fontWeight: '600', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Уроки
                            </div>
                            {sidebarLessons.length > 0 ? (
                                sidebarLessons.map((lessonItem, index) => {
                                    const levelLabel = lessonItem.level_name || `Уровень ${lessonItem.level_id || ''}`.trim();
                                    const previousLesson = sidebarLessons[index - 1];
                                    const previousLevelLabel = previousLesson
                                        ? previousLesson.level_name || `Уровень ${previousLesson.level_id || ''}`.trim()
                                        : null;
                                    const showLevelDivider = levelLabel && levelLabel !== previousLevelLabel;

                                    return (
                                    <React.Fragment key={lessonItem.id}>
                                        {showLevelDivider && (
                                            <div className="lesson-sidebar-level-divider">
                                                {levelLabel}
                                            </div>
                                        )}
                                        <div style={{ marginBottom: '4px' }}>
                                        {/* Заголовок урока */}
                                        <div
                                            ref={lesson?.id === lessonItem.id ? activeSidebarLessonRef : null}
                                            className={`lesson-sidebar-lesson ${lesson?.id === lessonItem.id ? 'active' : ''}`}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between'
                                            }}
                                        >
                                            <div 
                                                onClick={() => {
                                                    if (lesson?.id !== lessonItem.id) {
                                                        navigate(getStudentLessonPath(lessonItem));
                                                    }
                                                }}
                                                style={{ flex: 1, cursor: 'pointer' }}
                                            >
                                                <div className="lesson-sidebar-lesson-title">{lessonItem.title}</div>
                                                <div className="lesson-sidebar-lesson-level">{lessonItem.level_name}</div>
                                            </div>
                                            
                                            {/* Стрелочка для раскрытия - только для текущего урока с блоками */}
                                            {lesson?.id === lessonItem.id && lesson && lesson.hasBlocks && lesson.blocks && lesson.blocks.length > 0 && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedSidebarLessons(prev => ({
                                                            ...prev,
                                                            [lessonItem.id]: !prev[lessonItem.id]
                                                        }));
                                                    }}
                                                    style={{
                                                        background: 'transparent',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        padding: '8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        color: '#1976d2',
                                                        transform: expandedSidebarLessons[lessonItem.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.2s',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    ▼
                                                </button>
                                            )}
                                        </div>
                                        
                                        {/* Подразделы текущего урока - показываем только если раскрыто */}
                                        {lesson?.id === lessonItem.id && expandedSidebarLessons[lessonItem.id] && (
                                            <div style={{ paddingLeft: '15px', marginTop: '5px', marginBottom: '10px' }}>
                                                {/* Проверяем, есть ли блоки у урока */}
                                                {lesson && lesson.hasBlocks && lesson.blocks ? (
                                                    // Отображаем блоки
                                                    lesson.blocks.map((block, blockIdx) => (
                                                        <div key={blockIdx}>
                                                            <div
                                                                ref={currentBlockIndex === blockIdx ? activeSidebarBlockRef : null}
                                                                onClick={() => setCurrentBlockIndex(blockIdx)}
                                                                style={{
                                                                    padding: '8px 10px',
                                                                    marginBottom: '4px',
                                                                    cursor: 'pointer',
                                                                    borderRadius: '4px',
                                                                    background: currentBlockIndex === blockIdx ? '#f0f7ff' : 'transparent',
                                                                    color: currentBlockIndex === blockIdx ? '#1976d2' : '#666',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500',
                                                                    transition: 'all 0.2s',
                                                                    borderLeft: currentBlockIndex === blockIdx ? '2px solid #1976d2' : '2px solid transparent'
                                                                }}
                                                            >
                                                                {block.title}
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    // Старая структура
                                                    <>
                                                {hasSentences && (
                                                    <div
                                                        ref={activeSection === 'sentences' ? activeSidebarBlockRef : null}
                                                        onClick={() => scrollToSection('sentences')}
                                                        style={{
                                                            padding: '8px 10px',
                                                            marginBottom: '4px',
                                                            cursor: 'pointer',
                                                            borderRadius: '4px',
                                                            background: activeSection === 'sentences' ? '#f0f7ff' : 'transparent',
                                                            color: activeSection === 'sentences' ? '#1976d2' : '#666',
                                                            fontSize: '12px',
                                                            transition: 'all 0.2s',
                                                            borderLeft: activeSection === 'sentences' ? '2px solid #1976d2' : '2px solid transparent'
                                                        }}
                                                    >
                                                        Тренировка «Только новое»
                                                    </div>
                                                )}

                                                {hasTexts && lesson.texts.map((text, idx) => (
                                                    <div key={idx}>
                                                        <div
                                                            ref={activeSection === `text-${idx}` ? activeSidebarBlockRef : null}
                                                            onClick={() => scrollToSection(`text-${idx}`)}
                                                            style={{
                                                                padding: '8px 10px',
                                                                marginBottom: '4px',
                                                                cursor: 'pointer',
                                                                borderRadius: '4px',
                                                                background: activeSection === `text-${idx}` ? '#f0f7ff' : 'transparent',
                                                                color: activeSection === `text-${idx}` ? '#1976d2' : '#666',
                                                                fontSize: '12px',
                                                                fontWeight: '500',
                                                                transition: 'all 0.2s',
                                                                borderLeft: activeSection === `text-${idx}` ? '2px solid #1976d2' : '2px solid transparent'
                                                            }}
                                                        >
                                                            {text.title}
                                                        </div>
                                                        
                                                        {text.exercises && text.exercises.length > 0 && text.exercises.map((ex, exIdx) => (
                                                            <div
                                                                key={exIdx}
                                                                ref={activeSection === `exercise-${idx}-${exIdx}` ? activeSidebarBlockRef : null}
                                                                onClick={() => scrollToSection(`exercise-${idx}-${exIdx}`)}
                                                                style={{
                                                                    padding: '6px 10px 6px 25px',
                                                                    marginBottom: '3px',
                                                                    cursor: 'pointer',
                                                                    borderRadius: '4px',
                                                                    background: activeSection === `exercise-${idx}-${exIdx}` ? '#fff9e6' : 'transparent',
                                                                    color: activeSection === `exercise-${idx}-${exIdx}` ? '#d48806' : '#888',
                                                                    fontSize: '11px',
                                                                    transition: 'all 0.2s',
                                                                    borderLeft: activeSection === `exercise-${idx}-${exIdx}` ? '2px solid #d48806' : '2px solid transparent'
                                                                }}
                                                            >
                                                                {ex.title}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}

                                                {hasVideos && (
                                                    <div
                                                        ref={activeSection === 'videos' ? activeSidebarBlockRef : null}
                                                        onClick={() => scrollToSection('videos')}
                                                        style={{
                                                            padding: '8px 10px',
                                                            marginBottom: '4px',
                                                            cursor: 'pointer',
                                                            borderRadius: '4px',
                                                            background: activeSection === 'videos' ? '#f0f7ff' : 'transparent',
                                                            color: activeSection === 'videos' ? '#1976d2' : '#666',
                                                            fontSize: '12px',
                                                            transition: 'all 0.2s',
                                                            borderLeft: activeSection === 'videos' ? '2px solid #1976d2' : '2px solid transparent'
                                                        }}
                                                    >
                                                        Видео
                                                    </div>
                                                )}
                                                </>
                                                )}
                                            </div>
                                        )}
                                        </div>
                                    </React.Fragment>
                                    );
                                })
                            ) : (
                                <div style={{ padding: '20px', textAlign: 'center', fontSize: '13px', color: '#999' }}>
                                    Нет уроков
                                </div>
                            )}
                        </>
                    )}
                    {!backTo && !sidebarSelectedArea && (
                        <div style={{ padding: '20px', textAlign: 'center', fontSize: '13px', color: '#999' }}>
                            Выберите область обучения
                        </div>
                    )}
                </div>
            </aside>

            {/* Основной контент урока */}
            <main className="student-content" style={{ padding: '30px', background: '#f5f5f5' }}>
                <div style={{ minHeight: 'calc(100vh - 70px)', maxWidth: '1200px', margin: '0 auto' }}>
                    {backTo && (
                        <div style={{ marginBottom: '20px' }}>
                            <button
                                type="button"
                                onClick={() => navigate(backTo)}
                                style={{
                                    padding: '10px 16px',
                                    background: 'white',
                                    border: '1px solid #e8e8e8',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#2c3e50',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}
                            >
                                ← Назад к материалам
                            </button>
                        </div>
                    )}

                {/* Проверяем, есть ли блоки у урока */}
                {lesson.hasBlocks && lesson.blocks ? (
                    // НОВАЯ БЛОЧНАЯ СТРУКТУРА - ПОСТРАНИЧНОЕ ОТОБРАЖЕНИЕ
                    <>
                        {/* Отображаем только текущий блок */}
                        {lesson.blocks[currentBlockIndex] && (() => {
                            const block = lesson.blocks[currentBlockIndex];
                            const blockIdx = currentBlockIndex;
                            if (block.block_type === 'theory') {
                                return (
                                    <section key={blockIdx} id={`block-${blockIdx}`} style={{ marginBottom: '40px' }}>
                                        <div style={{ 
                                            background: 'white',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                        }}>
                                            <div style={{ 
                                                padding: '20px',
                                                borderBottom: '2px solid #f0f0f0',
                                                background: '#fafafa'
                                            }}>
                                                <h2 style={{ 
                                                    fontSize: '20px',
                                                    margin: 0,
                                                    color: '#2c3e50'
                                                }}>
                                                    {block.title}
                                                </h2>
                                            </div>
                                            <div 
                                                className="theory-content"
                                                style={{ padding: '30px 60px', lineHeight: '1.8' }}
                                                dangerouslySetInnerHTML={{ __html: block.content }}
                                            />
                                        </div>
                                    </section>
                                );
                            } else if (block.block_type === 'sentences') {
                                return (
                                    <section key={blockIdx} id={`block-${blockIdx}`} style={{ marginBottom: '40px' }}>
                                        <div style={{ 
                                            background: 'white',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                        }}>
                                            <div style={{ 
                                                padding: '20px',
                                                borderBottom: '2px solid #f0f0f0',
                                                background: '#fafafa'
                                            }}>
                                                <h2 style={{ 
                                                    fontSize: '20px',
                                                    margin: 0,
                                                    color: '#2c3e50'
                                                }}>
                                                    {block.title}
                                                </h2>
                                                {block.description && (
                                                    <p style={{ 
                                                        fontSize: '14px',
                                                        color: '#666',
                                                        margin: '10px 0 0 0',
                                                        lineHeight: 1.6,
                                                        whiteSpace: 'pre-wrap'
                                                    }}>
                                                        {block.description}
                                                    </p>
                                                )}
                                            </div>
                                            {block.sentences &&
                                                block.sentences.map((sentence, idx) =>
                                                    renderSentenceWithParts(sentence, idx, `block-${blockIdx}`)
                                                )}
                                            {!!Number(block.combined_speech_task) &&
                                                block.id &&
                                                block.sentences &&
                                                block.sentences.length > 0 && (
                                                    <div
                                                        style={{
                                                            borderTop: '2px dashed #e8e8e8',
                                                            background: '#fafafa',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                padding: '14px 20px 6px',
                                                                fontSize: '13px',
                                                                color: '#555',
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            Итоговое задание
                                                        </div>
                                                        <div style={{ padding: '0 20px 12px', fontSize: '13px', color: '#666' }}>
                                                            Ниже — дополнительно: одной записью произнесите все предложения блока подряд
                                                            (сначала обычно запишите каждую строку выше, затем — всё вместе).
                                                        </div>
                                                        {renderSentenceWithParts(
                                                            {
                                                                sentence_en: (block.sentences || [])
                                                                    .map((s) =>
                                                                        String(s.sentence_en || '')
                                                                            .replace(/\s*\|\s*/g, ' ')
                                                                            .trim()
                                                                    )
                                                                    .filter(Boolean)
                                                                    .join('|'),
                                                                sentence_ru: (block.sentences || [])
                                                                    .map((s) => {
                                                                        const ru = String(s.sentence_ru || '')
                                                                            .replace(/\s*\|\s*/g, ' ')
                                                                            .trim();
                                                                        const en = String(s.sentence_en || '')
                                                                            .replace(/\s*\|\s*/g, ' ')
                                                                            .trim();
                                                                        return ru || en;
                                                                    })
                                                                    .filter(Boolean)
                                                                    .join('|'),
                                                                voice_id:
                                                                    block.sentences[0].voice_id ||
                                                                    'en-US-JennyNeural',
                                                                audio_url: null,
                                                                id: null,
                                                                recordingKey: `b-${block.id}`,
                                                                rowLabel: 'Все',
                                                            },
                                                            0,
                                                            `block-${blockIdx}-combined`
                                                        )}
                                                    </div>
                                                )}
                                        </div>
                                    </section>
                                );
                            } else if (block.block_type === 'text') {
                                const text = block.text;
                                if (!text) return null;
                                
                                return (
                                    <section key={blockIdx} id={`block-${blockIdx}`} style={{ marginBottom: '40px' }}>
                                        <div style={{ 
                                            background: 'white',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                        }}>
                                            <div style={{ 
                                                padding: '20px',
                                                borderBottom: '2px solid #f0f0f0',
                                                background: '#fafafa'
                                            }}>
                                                <h2 style={{ 
                                                    fontSize: '20px',
                                                    margin: 0,
                                                    color: '#2c3e50'
                                                }}>
                                                    {text.title}
                                                </h2>
                                                {text.description && (
                                                    <p style={{ 
                                                        fontSize: '13px',
                                                        color: '#777',
                                                        margin: '5px 0 0 0'
                                                    }}>
                                                        {text.description}
                                                    </p>
                                                )}
                                            </div>
                                            
                                            {/* Картинка */}
                                            {text.image_url && (
                                                <div style={{ padding: '20px', textAlign: 'center', background: '#fafafa' }}>
                                                    <img 
                                                        src={text.image_url.startsWith('http') ? text.image_url : `${API_ORIGIN}${text.image_url}`}
                                                        alt={text.title}
                                                        style={{ 
                                                            maxWidth: '100%',
                                                            maxHeight: '400px',
                                                            borderRadius: '8px',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                        }}
                                                        onError={(e) => e.target.style.display = 'none'}
                                                    />
                                                </div>
                                            )}

                                            {/* Текст целиком */}
                                            {text.full_text_en && (
                                                <div style={{ padding: '30px 60px' }}>
                                                    {renderFullText(text, blockIdx)}
                                                    {renderAudioPlayer(text, blockIdx)}
                                                    {renderTextBlockRecording(block, blockIdx)}
                                                </div>
                                            )}
                                        </div>

                                        {/* Упражнения */}
                                        {text.exercises && text.exercises.map((exercise, exIdx) => (
                                            <div 
                                                key={exIdx}
                                                id={`exercise-${blockIdx}-${exIdx}`}
                                                style={{ 
                                                    background: 'white',
                                                    borderRadius: '12px',
                                                    padding: '25px',
                                                    marginTop: '25px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                    border: '1px solid #e8e8e8'
                                                }}
                                            >
                                                <div>
                                                    <div style={{ 
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        marginBottom: '15px'
                                                    }}>
                                                        <div style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            background: '#f0f7ff',
                                                            border: '1px solid #d0e7ff',
                                                            borderRadius: '8px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: '#4a90e2',
                                                            fontSize: '18px'
                                                        }}>
                                                            <FiEdit3 />
                                                        </div>
                                                        <h3 style={{ 
                                                            fontSize: '18px',
                                                            margin: 0,
                                                            color: '#2c3e50',
                                                            fontWeight: '600'
                                                        }}>
                                                            {exercise.title}
                                                        </h3>
                                                    </div>
                                                    {exercise.instructions && (
                                                        <p style={{ 
                                                            fontSize: '14px',
                                                            color: '#666',
                                                            marginBottom: '15px',
                                                            lineHeight: '1.6'
                                                        }}>
                                                            {exercise.instructions}
                                                        </p>
                                                    )}
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={() => handleOpenExercise(exercise, blockIdx, exIdx)}
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '8px'
                                                        }}
                                                    >
                                                        <FiEdit3 /> Начать упражнение
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </section>
                                );
                            } else if (block.block_type === 'video') {
                                const video = block.video;
                                if (!video) return null;

                                const getVideoId = (url) => {
                                    if (!url) return null;
                                    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
                                    if (youtubeMatch) return { type: 'youtube', id: youtubeMatch[1] };
                                    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
                                    if (vimeoMatch) return { type: 'vimeo', id: vimeoMatch[1] };
                                    return null;
                                };

                                const isUploadedVideo = video.video_type === 'upload';
                                const videoInfo = !isUploadedVideo ? getVideoId(video.video_url) : null;
                                
                                return (
                                    <section key={blockIdx} id={`block-${blockIdx}`} style={{ marginBottom: '40px' }}>
                                        <div style={{ 
                                            background: 'white',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                        }}>
                                            <div style={{ 
                                                padding: '20px',
                                                borderBottom: '2px solid #f0f0f0',
                                                background: '#fafafa'
                                            }}>
                                                <h2 style={{ 
                                                    fontSize: '20px',
                                                    margin: 0,
                                                    color: '#2c3e50'
                                                }}>
                                                    {video.title}
                                                </h2>
                                                {video.description && (
                                                    <p style={{ 
                                                        fontSize: '13px',
                                                        color: '#777',
                                                        margin: '5px 0 0 0'
                                                    }}>
                                                        {video.description}
                                                    </p>
                                                )}
                                            </div>
                                            {isUploadedVideo && (
                                                <div style={{ padding: '20px' }}>
                                                    <video
                                                        src={video.video_url.startsWith('http')
                                                            ? video.video_url
                                                            : `${API_ORIGIN}${video.video_url}`}
                                                        controls
                                                        style={{
                                                            width: '100%',
                                                            maxHeight: '480px',
                                                            display: 'block'
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            {!isUploadedVideo && videoInfo && (
                                                <div style={{ padding: '20px' }}>
                                                    <div style={{
                                                        position: 'relative',
                                                        paddingBottom: '56.25%',
                                                        height: 0,
                                                        overflow: 'hidden',
                                                        borderRadius: '8px'
                                                    }}>
                                                        {videoInfo.type === 'youtube' && (
                                                            <iframe
                                                                src={`https://www.youtube.com/embed/${videoInfo.id}`}
                                                                frameBorder="0"
                                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                allowFullScreen
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    width: '100%',
                                                                    height: '100%'
                                                                }}
                                                            />
                                                        )}
                                                        {videoInfo.type === 'vimeo' && (
                                                            <iframe
                                                                src={`https://player.vimeo.com/video/${videoInfo.id}`}
                                                                frameBorder="0"
                                                                allow="autoplay; fullscreen; picture-in-picture"
                                                                allowFullScreen
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    width: '100%',
                                                                    height: '100%'
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                );
                            } else if (block.block_type === 'exercises') {
                                const exercises = block.exercises;
                                if (!exercises || exercises.length === 0) return null;
                                
                                return (
                                    <section key={blockIdx} id={`block-${blockIdx}`} style={{ marginBottom: '40px' }}>
                                        <div style={{ 
                                            background: 'white',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                        }}>
                                            <div style={{ 
                                                padding: '20px',
                                                borderBottom: '2px solid #f0f0f0',
                                                background: '#fafafa'
                                            }}>
                                                <h2 style={{ 
                                                    fontSize: '20px',
                                                    margin: 0,
                                                    color: '#2c3e50'
                                                }}>
                                                    {block.title}
                                                </h2>
                                            </div>
                                            
                                            {/* Отображаем все упражнения развёрнуто */}
                                            {exercises.map((exercise, exIdx) => (
                                                <ExerciseItem
                                                    key={exIdx}
                                                    exercise={exercise}
                                                    exIdx={exIdx}
                                                    lessonId={effectiveLessonId}
                                                    initialAttempt={lessonExerciseAttempts && lessonExerciseAttempts[exercise.id]}
                                                    onRefreshAttempts={refreshExerciseAttempts}
                                                />
                                            ))}
                                        </div>
                                    </section>
                                );
                            } else if (block.block_type === 'dialog') {
                                const messages = block.messages;
                                const vocabulary = block.vocabulary || [];
                                if (!messages || messages.length === 0) return null;
                                
                                return (
                                    <section key={blockIdx} id={`block-${blockIdx}`} style={{ marginBottom: '40px' }}>
                                        <div style={{ 
                                            background: 'white',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                        }}>
                                            <div style={{ 
                                                padding: '20px',
                                                borderBottom: '2px solid #f0f0f0',
                                                background: '#fafafa'
                                            }}>
                                                <h2 style={{ 
                                                    fontSize: '20px',
                                                    margin: 0,
                                                    color: '#2c3e50'
                                                }}>
                                                    {block.title}
                                                </h2>
                                            </div>

                                            {/* Вводная лексика */}
                                            {vocabulary && vocabulary.length > 0 && (
                                                <div style={{ 
                                                    padding: '20px',
                                                    background: 'white',
                                                    borderBottom: '1px solid #f0f0f0'
                                                }}>
                                                    <h3 style={{ 
                                                        fontSize: '16px',
                                                        margin: '0 0 15px 0',
                                                        color: '#2c3e50',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px'
                                                    }}>
                                                        <FiBook size={18} />
                                                        Вводная лексика
                                                    </h3>
                                                    <div style={{ 
                                                        display: 'grid',
                                                        gap: '12px'
                                                    }}>
                                                        {vocabulary.map((item, vocabIdx) => {
                                                            const showVocabTranslation = showSentenceTranslations[`vocab-${blockIdx}-${vocabIdx}`];
                                                            
                                                            return (
                                                                <div
                                                                    key={vocabIdx}
                                                                    style={{
                                                                        background: 'white',
                                                                        padding: '14px 18px',
                                                                        borderRadius: '8px',
                                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                                                        border: '1px solid #eceff3'
                                                                    }}
                                                                >
                                                                    <div style={{ 
                                                                        fontSize: '15px', 
                                                                        fontWeight: '500', 
                                                                        color: '#2c3e50',
                                                                        marginBottom: showVocabTranslation ? '8px' : '0'
                                                                    }}>
                                                                        {item.text_ru || item.ru || item.text_en || item.en || ''}
                                                                    </div>
                                                                    
                                                                    {(item.text_en || item.en) && showVocabTranslation && (
                                                                        <div style={{ 
                                                                            fontSize: '13px', 
                                                                            color: '#8492a6',
                                                                            paddingTop: '8px',
                                                                            borderTop: '1px dashed #e0e6ed',
                                                                            fontStyle: 'italic'
                                                                        }}>
                                                                            {item.text_en || item.en}
                                                                        </div>
                                                                    )}
                                                                    
                                                                    <div style={{ 
                                                                        display: 'flex',
                                                                        gap: '6px',
                                                                        marginTop: '10px'
                                                                    }}>
                                                                        <button
                                                                            onClick={() => playSentence(item.text_en || item.en, item.voice_id, item.audio_url)}
                                                                            style={{
                                                                                background: 'white',
                                                                                color: '#1890ff',
                                                                                border: '1px solid #e0e6ed',
                                                                                borderRadius: '20px',
                                                                                padding: '6px 14px',
                                                                                cursor: 'pointer',
                                                                                fontSize: '12px',
                                                                                fontWeight: '500',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '4px',
                                                                                transition: 'all 0.2s',
                                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                                                            }}
                                                                            title="Прослушать"
                                                                            onMouseEnter={(e) => {
                                                                                e.currentTarget.style.background = '#1890ff';
                                                                                e.currentTarget.style.color = 'white';
                                                                                e.currentTarget.style.borderColor = '#1890ff';
                                                                            }}
                                                                            onMouseLeave={(e) => {
                                                                                e.currentTarget.style.background = 'white';
                                                                                e.currentTarget.style.color = '#1890ff';
                                                                                e.currentTarget.style.borderColor = '#e0e6ed';
                                                                            }}
                                                                        >
                                                                            <FiPlay size={12} style={{ display: 'block', flexShrink: 0 }} />
                                                                            Озвучка
                                                                        </button>
                                                                        
                                                                        {(item.text_en || item.en) && (
                                                                            <button
                                                                                onClick={() => {
                                                                                    const key = `vocab-${blockIdx}-${vocabIdx}`;
                                                                                    toggleSentenceTranslation(key);
                                                                                }}
                                                                                style={{
                                                                                    background: showVocabTranslation ? '#52c41a' : 'white',
                                                                                    color: showVocabTranslation ? 'white' : '#52c41a',
                                                                                    border: '1px solid',
                                                                                    borderColor: showVocabTranslation ? '#52c41a' : '#e0e6ed',
                                                                                    borderRadius: '20px',
                                                                                    padding: '6px 14px',
                                                                                    cursor: 'pointer',
                                                                                    fontSize: '12px',
                                                                                    fontWeight: '500',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: '4px',
                                                                                    transition: 'all 0.2s',
                                                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                                                                }}
                                                                                title={showVocabTranslation ? 'Скрыть перевод' : 'Показать перевод'}
                                                                            >
                                                                                <FiEye size={12} />
                                                                                {showVocabTranslation ? 'Скрыть' : 'Перевод'}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            <div style={{ 
                                                padding: '30px 20px',
                                                background: '#f5f7fa'
                                            }}>
                                                {messages.map((message, msgIdx) => {
                                                    const isLeft = msgIdx % 2 === 0;
                                                    const showTranslation = showSentenceTranslations[`dialog-${blockIdx}-${msgIdx}`];
                                                    
                                                    return (
                                                        <div
                                                            key={msgIdx}
                                                            style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: isLeft ? 'flex-start' : 'flex-end',
                                                                marginBottom: '20px'
                                                            }}
                                                        >
                                                            {/* Имя говорящего */}
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                marginBottom: '8px',
                                                                paddingLeft: isLeft ? '12px' : '0',
                                                                paddingRight: isLeft ? '0' : '12px'
                                                            }}>
                                                                <div style={{
                                                                    width: '32px',
                                                                    height: '32px',
                                                                    borderRadius: '50%',
                                                                    background: isLeft ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: 'white',
                                                                    fontSize: '14px',
                                                                    fontWeight: '700',
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                                    order: isLeft ? 0 : 1
                                                                }}>
                                                                    {message.speaker ? message.speaker.charAt(0).toUpperCase() : 'S'}
                                                                </div>
                                                                <span style={{
                                                                    fontSize: '13px',
                                                                    fontWeight: '600',
                                                                    color: '#666',
                                                                    order: isLeft ? 1 : 0
                                                                }}>
                                                                    {message.speaker}
                                                                </span>
                                                            </div>
                                                            
                                                            {/* Облачко сообщения */}
                                                            <div style={{
                                                                maxWidth: '75%',
                                                                position: 'relative'
                                                            }}>
                                                                <div style={{
                                                                    background: isLeft ? 'white' : '#667eea',
                                                                    padding: '14px 18px',
                                                                    borderRadius: isLeft ? '8px 18px 18px 18px' : '18px 8px 18px 18px',
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                                    position: 'relative'
                                                                }}>
                                                                    <div style={{ 
                                                                        fontSize: '15px', 
                                                                        fontWeight: '400', 
                                                                        color: isLeft ? '#2c3e50' : 'white',
                                                                        lineHeight: '1.5',
                                                                        marginBottom: message.message_en && showTranslation ? '10px' : '0'
                                                                    }}>
                                                                        {message.message_ru}
                                                                    </div>
                                                                    
                                                                    {message.message_en && showTranslation && (
                                                                        <div style={{ 
                                                                            fontSize: '13px', 
                                                                            color: isLeft ? '#8492a6' : 'rgba(255,255,255,0.85)',
                                                                            paddingTop: '8px',
                                                                            borderTop: isLeft ? '1px dashed #e0e6ed' : '1px dashed rgba(255,255,255,0.3)',
                                                                            fontStyle: 'italic'
                                                                        }}>
                                                                            {message.message_en}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* Кнопки управления */}
                                                                <div style={{ 
                                                                    display: 'flex',
                                                                    gap: '6px',
                                                                    marginTop: '8px',
                                                                    justifyContent: isLeft ? 'flex-start' : 'flex-end',
                                                                    paddingLeft: isLeft ? '8px' : '0',
                                                                    paddingRight: isLeft ? '0' : '8px'
                                                                }}>
                                                                    <button
                                                                        onClick={() => playSentence(message.message_en, message.voice_id, message.audio_url)}
                                                                        style={{
                                                                            background: 'white',
                                                                            color: '#667eea',
                                                                            border: '1px solid #e0e6ed',
                                                                            borderRadius: '20px',
                                                                            padding: '6px 14px',
                                                                            cursor: 'pointer',
                                                                            fontSize: '12px',
                                                                            fontWeight: '500',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '4px',
                                                                            transition: 'all 0.2s',
                                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                                                        }}
                                                                        title="Прослушать"
                                                                        onMouseEnter={(e) => {
                                                                            e.target.style.background = '#667eea';
                                                                            e.target.style.color = 'white';
                                                                            e.target.style.borderColor = '#667eea';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.target.style.background = 'white';
                                                                            e.target.style.color = '#667eea';
                                                                            e.target.style.borderColor = '#e0e6ed';
                                                                        }}
                                                                    >
                                                                        <span style={{ fontSize: '10px' }}>▶</span>
                                                                        Озвучка
                                                                    </button>
                                                                    
                                                                    {message.message_ru && (
                                                                        <button
                                                                            onClick={() => {
                                                                                const key = `dialog-${blockIdx}-${msgIdx}`;
                                                                                toggleSentenceTranslation(key);
                                                                            }}
                                                                            style={{
                                                                                background: showTranslation ? '#52c41a' : 'white',
                                                                                color: showTranslation ? 'white' : '#52c41a',
                                                                                border: '1px solid',
                                                                                borderColor: showTranslation ? '#52c41a' : '#e0e6ed',
                                                                                borderRadius: '20px',
                                                                                padding: '6px 14px',
                                                                                cursor: 'pointer',
                                                                                fontSize: '12px',
                                                                                fontWeight: '500',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '4px',
                                                                                transition: 'all 0.2s',
                                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                                                            }}
                                                                            title={showTranslation ? 'Скрыть перевод' : 'Показать перевод'}
                                                                        >
                                                                            <FiEye size={12} />
                                                                            {showTranslation ? 'Скрыть' : 'Перевод'}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </section>
                                );
                            }
                            return null;
                        })()}
                        
                        {/* Кнопки навигации между блоками */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '30px',
                            padding: '20px',
                            background: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                            <button
                                onClick={() => setCurrentBlockIndex(currentBlockIndex - 1)}
                                disabled={currentBlockIndex === 0}
                                style={{
                                    padding: '12px 24px',
                                    background: currentBlockIndex === 0 ? '#e0e0e0' : '#667eea',
                                    color: currentBlockIndex === 0 ? '#999' : 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '15px',
                                    fontWeight: '500',
                                    cursor: currentBlockIndex === 0 ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                ← Назад
                            </button>
                            
                            <div style={{ fontSize: '14px', color: '#666' }}>
                                Блок {currentBlockIndex + 1} из {lesson.blocks.length}
                            </div>
                            
                            <button
                                onClick={() => setCurrentBlockIndex(currentBlockIndex + 1)}
                                disabled={currentBlockIndex >= lesson.blocks.length - 1}
                                style={{
                                    padding: '12px 24px',
                                    background: currentBlockIndex >= lesson.blocks.length - 1 ? '#e0e0e0' : '#667eea',
                                    color: currentBlockIndex >= lesson.blocks.length - 1 ? '#999' : 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '15px',
                                    fontWeight: '500',
                                    cursor: currentBlockIndex >= lesson.blocks.length - 1 ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                Далее →
                            </button>
                        </div>
                    </>
                ) : (
                    // СТАРАЯ СТРУКТУРА (без блоков)
                    <>
                {/* Отдельные предложения */}
                {hasSentences && (
                    <section id="sentences" style={{ marginBottom: '40px' }}>
                        <div style={{ 
                            background: 'white',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                            <div style={{ 
                                padding: '20px',
                                borderBottom: '2px solid #f0f0f0',
                                background: '#fafafa'
                            }}>
                                <h2 style={{ 
                                    fontSize: '20px',
                                    margin: 0,
                                    color: '#2c3e50'
                                }}>
                                    Тренировка «Только новое»
                                </h2>
                            </div>
                            
                            {lesson.standaloneSentences.map((sentence, idx) => 
                                renderSentenceWithParts(sentence, idx, 'standalone')
                            )}
                        </div>
                    </section>
                )}

                {/* Тексты */}
                {hasTexts && lesson.texts.map((text, textIdx) => (
                    <section key={textIdx} id={`text-${textIdx}`} style={{ marginBottom: '40px' }}>
                        <div style={{ 
                            background: 'white',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                            <div style={{ 
                                padding: '20px',
                                borderBottom: '2px solid #f0f0f0',
                                background: '#fafafa'
                            }}>
                                <h2 style={{ 
                                    fontSize: '20px',
                                    margin: 0,
                                    color: '#2c3e50'
                                }}>
                                    {text.title}
                                </h2>
                                {text.description && (
                                    <p style={{ 
                                        fontSize: '13px',
                                        color: '#777',
                                        margin: '5px 0 0 0'
                                    }}>
                                        {text.description}
                                    </p>
                                )}
                            </div>
                            
                            {/* Картинка */}
                            {text.image_url && (
                                <div style={{ padding: '20px', textAlign: 'center', background: '#fafafa' }}>
                                    <img 
                                        src={text.image_url.startsWith('http') ? text.image_url : `${API_ORIGIN}${text.image_url}`}
                                        alt={text.title}
                                        style={{ 
                                            maxWidth: '100%',
                                            maxHeight: '400px',
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }}
                                        onError={(e) => {
                                            console.error('Ошибка загрузки изображения:', text.image_url);
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}

                            {/* Текст целиком */}
                            {text.full_text_en && (
                                <div style={{ padding: '30px 60px' }}>
                                    {renderFullText(text, textIdx)}
                                    {renderAudioPlayer(text, textIdx)}
                                    {text.block_id && renderTextBlockRecording({ id: text.block_id }, textIdx)}
                                </div>
                            )}
                        </div>

                        {/* Упражнения */}
                        {text.exercises && text.exercises.map((exercise, exIdx) => (
                            <div 
                                key={exIdx}
                                id={`exercise-${textIdx}-${exIdx}`}
                                style={{ 
                                    background: 'white',
                                    borderRadius: '12px',
                                    padding: '25px',
                                    marginTop: '25px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    border: '1px solid #e8e8e8'
                                }}
                            >
                                <div>
                                    <div style={{ 
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        marginBottom: '15px'
                                    }}>
                                        <div style={{
                                            width: '36px',
                                            height: '36px',
                                            background: '#f0f7ff',
                                            border: '1px solid #d0e7ff',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#4a90e2',
                                            fontSize: '18px'
                                        }}>
                                            <FiEdit3 />
                                        </div>
                                        <h3 style={{ 
                                            fontSize: '18px',
                                            margin: 0,
                                            color: '#2c3e50',
                                            fontWeight: '600'
                                        }}>
                                            {exercise.title}
                                        </h3>
                                    </div>
                                    
                                    {exercise.instructions && (
                                        <p style={{ 
                                            fontSize: '14px',
                                            color: '#666',
                                            marginBottom: '15px',
                                            lineHeight: '1.6'
                                        }}>
                                            {exercise.instructions}
                                        </p>
                                    )}
                                    
                                    <div style={{ 
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        flexWrap: 'wrap',
                                        marginBottom: '20px'
                                    }}>
                                        <div style={{
                                            background: '#f0f7ff',
                                            padding: '6px 14px',
                                            borderRadius: '20px',
                                            fontSize: '13px',
                                            color: '#4a90e2',
                                            fontWeight: '500',
                                            border: '1px solid #d0e7ff'
                                        }}>
                                            Тип: {exercise.exercise_type === 'multiple_choice' ? 'Выбор ответа' : 
                                                  exercise.exercise_type === 'true_false' ? 'Правда / ложь' :
                                                  exercise.exercise_type === 'fill_gaps' ? 'Заполнить пропуски' :
                                                  exercise.exercise_type === 'match' ? 'Сопоставление' :
                                                  exercise.exercise_type === 'arrange' ? 'Расставить по порядку' :
                                                  exercise.exercise_type === 'text_answer' ? 'Текстовый ответ' :
                                                  exercise.exercise_type === 'phonetic_dictation' ? 'Фонетический диктант' :
                                                  exercise.exercise_type}
                                        </div>
                                        
                                        {exercise.questions && (
                                            <div style={{
                                                background: '#f6ffed',
                                                padding: '6px 14px',
                                                borderRadius: '20px',
                                                fontSize: '13px',
                                                color: '#52c41a',
                                                fontWeight: '500',
                                                border: '1px solid #d9f7be'
                                            }}>
                                                {exercise.questions.length} {exercise.questions.length === 1 ? 'вопрос' : 'вопросов'}
                                            </div>
                                        )}
                                        {(() => {
                                            const att = lessonExerciseAttempts?.[exercise.id];
                                            const lbl = formatStudentExerciseProgressLabel(exercise.exercise_type, att);
                                            if (!lbl) return null;
                                            return (
                                                <div style={{
                                                    background: '#e6f7ff',
                                                    padding: '6px 14px',
                                                    borderRadius: '20px',
                                                    fontSize: '13px',
                                                    color: '#1890ff',
                                                    fontWeight: '500',
                                                    border: '1px solid #91d5ff'
                                                }}>
                                                    {lbl}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                    
                                    <button 
                                        style={{
                                            background: '#4a90e2',
                                            color: 'white',
                                            border: 'none',
                                            padding: '12px 28px',
                                            borderRadius: '8px',
                                            fontSize: '15px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            boxShadow: '0 2px 6px rgba(74, 144, 226, 0.3)',
                                            transition: 'all 0.3s',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '10px'
                                        }}
                                        onClick={() => handleOpenExercise(exercise, textIdx, exIdx)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(74, 144, 226, 0.4)';
                                            e.currentTarget.style.background = '#3a7bc8';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(74, 144, 226, 0.3)';
                                            e.currentTarget.style.background = '#4a90e2';
                                        }}
                                    >
                                        <span>Начать упражнение</span>
                                        <span style={{ fontSize: '18px' }}>→</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </section>
                ))}

                {/* Видео */}
                {hasVideos && (
                    <section id="videos" style={{ marginBottom: '40px' }}>
                        <div style={{ 
                            background: 'white',
                            borderRadius: '8px',
                            padding: '25px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                            <h2 style={{ fontSize: '20px', marginBottom: '20px', color: '#2c3e50' }}>
                                Видео
                            </h2>
                            {lesson.videos.map((video, idx) => (
                                <div 
                                    key={idx}
                                    style={{ 
                                        padding: '15px',
                                        background: '#f8f9fa',
                                        borderRadius: '6px',
                                        marginBottom: '15px'
                                    }}
                                >
                                    <h3 style={{ fontSize: '16px', marginBottom: '8px' }}>
                                        {video.title}
                                    </h3>
                                    {video.description && (
                                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                                            {video.description}
                                        </p>
                                    )}
                                    <a 
                                        href={video.video_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="btn"
                                        style={{ display: 'inline-block', textDecoration: 'none' }}
                                    >
                                        Открыть видео →
                                    </a>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
                </>
                )}

                {/* Модальное окно упражнения */}
                {renderExerciseModal()}

                {/* Кнопка для открытия мобильного сайдбара */}
                <button 
                    className="mobile-sidebar-toggle" 
                    onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
                    aria-label="Меню уроков"
                >
                    <FiList size={24} />
                </button>
                </div>
            </main>
        </>
    );
};

export default StudentDashboard;
